import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  BuildMode,
  DetailSelection,
  GameMode,
  GameState,
  HomeCity,
  HospitalPatient,
  LayerToggles,
  MainPanelTab,
  Mission,
  MissionRequirement,
  SandboxConfig,
  SpeedMultiplier,
  Station,
  StationKind,
  TimeFormat,
  ToastMessage,
  UiPrefs,
  Vehicle,
  VehicleModuleInstance,
  VehicleStatus,
  VehicleType,
} from './types';
import { getLevelUpCost, clampLevel, getRadiusKm } from '@/data/leveling';
import {
  getCurrentTier,
  getNextTier,
  getMaxTierLevel,
  effectiveDailyUpkeep,
  stationAllowsEmsVehicles,
  stationAllowsRepair,
} from '@/data/stations';
import { extensionById } from '@/data/stationExtensions';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { getEffectiveBuildTimeMs } from '@/data/buildTime';
import { pointsFor } from '@/data/points';
import {
  MISSION_PENALTY_MODIFIERS,
  MISSION_DIFFICULTY_REWARDS,
  HOSPITAL_TREATMENT_MINUTES,
  SANDBOX_INTERVAL_DEFAULT_SEC,
  SANDBOX_INTERVAL_MIN_SEC,
  SANDBOX_INTERVAL_MAX_SEC,
  ESCALATION_COOLDOWN_MS,
  ESCALATION_PROGRESS_FLOOR,
  ESCALATION_TIMEOUT_BONUS_FACTOR,
} from '@/data/missions';
import { getMissionCatalogEntry } from '@/data/missionCatalog';
import {
  REPAIR_DISABLE_THRESHOLD,
  repairCostFor,
  repairDurationMs,
  type RepairMode,
} from '@/data/repair';
import type { Hydrant } from '@/data/hydrants';
import { getCityFactor } from '@/data/cityFactor';
import type {
  GameStats,
  Measure,
  NpcStation,
  RadioMessage,
  RadioRequest,
  EscalationEntry,
} from './types';
import {
  readActiveSlotId,
  writeActiveSlotId,
  writeSlotMeta,
  slotKey,
} from '@/lib/saveSlots';
import {
  combinedEffects,
  moduleById,
  moduleSatisfies,
  migrateLegacyModuleId,
} from '@/data/vehicleModules';
import { makeMeasuresForCatalog } from '@/data/measures';
import {
  USED_REFRESH_FEE,
  USED_REFRESH_INTERVAL_MS,
  generateUsedListings,
} from '@/data/usedMarket';
import {
  pickCatalogEntry,
  spawnPosition,
  isActiveMissionStatus,
} from '@/data/missionGenerator';
import { getAlarmDurationMs } from '@/data/alarmDuration';
import {
  HANDOVER_DURATION_MS,
  applyResourceDrain,
  isTransportVehicle,
  pruneExpiredPatients,
} from './vehicleStateMachine';
import { playSoundEvent } from '@/lib/audio';
import {
  GAME_DAY_MS,
  GAME_WEEK_MS,
  LOAN_INTEREST_RATE_WEEKLY,
  LOAN_MIN_AMOUNT,
  LOAN_MAX_AMOUNT,
  applyWeeklyInterest,
  availableLoanCapacity,
} from '@/data/loans';
import type { Loan } from './types';

export const DEFAULT_CENTER = { lat: 52.52, lng: 13.405 };
export const DEFAULT_ZOOM = 13;
const HOSPITAL_TREATMENT_MS = HOSPITAL_TREATMENT_MINUTES * 60_000;

/** Phase 8.4.1 — Reconnaissance / Erkundung duration (game-time). */
export const RECONNAISSANCE_DURATION_MS = 30_000;

/** Phase 12.3 — Werkstatt-Modul-Tausch dauert 5 Minuten Spielzeit. */
export const MODULE_SWAP_DURATION_MS = 5 * 60_000;
/** Phase 12.3 — Aufschlag auf Modul-Preis bei nachträglichem Tausch. */
export const MODULE_SWAP_SURCHARGE = 0.5;
/** Phase 12.3 — Refund bei Entfernen eines nachgekauften Moduls. */
export const MODULE_SWAP_REFUND_RATE = 0.3;
/** Phase 12.3 — Refund-Anteil bei Cancel des laufenden Tauschs. */
export const MODULE_SWAP_CANCEL_REFUND_RATE = 0.5;

/** Phase 10.4.2 — random "Maxi M." style name for booked persons. */
const FIRST_NAMES = [
  'Alex', 'Maxi', 'Charlie', 'Sam', 'Jamie', 'Robin', 'Toni', 'Kim',
  'Luca', 'Nicki', 'Pat', 'Quinn', 'Sasha', 'Lou',
];
const LAST_INITIALS = ['B.', 'F.', 'H.', 'K.', 'M.', 'S.', 'W.', 'Z.', 'L.', 'R.'];
function makeRandomName(): string {
  const f = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const l = LAST_INITIALS[Math.floor(Math.random() * LAST_INITIALS.length)];
  return `${f} ${l}`;
}

export const STARTING_CREDITS: Record<GameMode, number> = {
  normal: 3_000_000,
  realistic: 2_000_000,
  sandbox: 10_000_000,
};

const defaultUiPrefs = (): UiPrefs => ({
  layerToggles: {
    missions: true,
    buildings3d: true,
    stations: true,
    radius: true,
    hydrants: true,
  },
  muted: false,
  audioVolume: 0.6,
  timeFormat: '24h',
  reduceMotion:
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  onboardingCompleted: false,
  // Phase 13.10 — Default off; NPC-Marker erscheinen nur bei Toggle.
  showAcquirableStations: false,
});

const defaultSandboxConfig = (): SandboxConfig => ({
  missionIntervalSec: SANDBOX_INTERVAL_DEFAULT_SEC,
});

const defaultOrgStats = () => ({
  missionsCompleted: 0,
  missionsFailed: 0,
  arrestsCompleted: 0,
  byCode: {} as Record<string, number>,
});

const defaultStats = (): GameStats => ({
  totalIncome: 0,
  spentOnStations: 0,
  spentOnVehicles: 0,
  spentOnRepairs: 0,
  spentOnUpkeep: 0,
  spentOnNpcAcquisition: 0,
  spentOnModules: 0,
  spentOnLoanInterest: 0,
  missionsCompleted: 0,
  missionsFailed: 0,
  missionsByCode: {},
  playtimeSec: 0,
  byOrganization: {
    fire: defaultOrgStats(),
    ems: defaultOrgStats(),
    police: defaultOrgStats(),
  },
});

/** Phase 7.2.1 — Mode base interval (real-seconds). Modulated by city/level/stations. */
const MODE_BASE_INTERVAL_SEC: Record<GameMode, number> = {
  normal: 180,
  realistic: 120,
  sandbox: 60, // overridden by sandboxConfig.missionIntervalSec
};

/** Cap for active missions, dynamically scaled by ready-station count. */
function dynamicMaxMissions(readyStationCount: number): number {
  return Math.min(20, 5 + readyStationCount * 2);
}

/** Compute the next-spawn interval in ms (gameTime) using the situational model. */
function computeSpawnIntervalMs(
  state: GameState,
  readyStationCount: number,
): number {
  if (state.mode === 'sandbox') {
    return state.sandboxConfig.missionIntervalSec * 1000;
  }
  const baseSec = MODE_BASE_INTERVAL_SEC[state.mode];
  const cityFactor = state.homeCity
    ? getCityFactor(state.homeCity.population, state.homeCity.addressType)
    : 1.0;
  const stationFactor = Math.min(2.0, 0.7 + readyStationCount * 0.15);
  const levelFactor = Math.max(0.6, 1 - state.level * 0.02);
  const denom = cityFactor * stationFactor * levelFactor;
  if (denom <= 0) return baseSec * 1000;
  // Add ±15 % jitter so it doesn't feel mechanical.
  const jitter = 0.85 + Math.random() * 0.3;
  return Math.round(((baseSec * 1000) / denom) * jitter);
}

interface GameActions {
  setMode: (mode: GameMode) => void;
  setSpeedMultiplier: (m: SpeedMultiplier) => void;
  togglePause: () => void;
  setMapView: (center: { lat: number; lng: number }, zoom: number) => void;

  addCredits: (amount: number) => void;
  spendCredits: (amount: number) => boolean;

  addPoints: (delta: number) => void;
  awardPoints: (action: Parameters<typeof pointsFor>[0]) => void;

  setHomeCity: (city: HomeCity) => void;
  buyLevelUp: () => boolean;
  setBuildMode: (mode: BuildMode) => void;

  buildStation: (input: {
    kind: StationKind;
    name: string;
    position: { lat: number; lng: number };
  }) => Station | null;

  upgradeStation: (stationId: string) => boolean;
  /** Phase 8.2 — Buy a station extension. Returns true on success. */
  buildStationExtension: (stationId: string, extensionId: string) => boolean;
  recomputeUpkeep: () => void;
  removeStation: (stationId: string) => void;
  /**
   * Phase 9.5 — Sell a station back to the city.
   *  - Completed station: 50 % refund of `totalSpent` + 50 % of vehicle costs.
   *  - Under-construction station: 80 % refund of currently-paid amount only.
   * Returns the refunded credits (0 if station not found).
   */
  sellStation: (stationId: string) => number;

  addVehicle: (vehicle: Vehicle) => void;
  /**
   * Phase 8.5 — Optional `opts` accepts a custom display name/Rufzeichen and a
   * `used` flag (60 % cost, condition starts at 0.6). Backwards compatible with
   * the legacy 2-arg form. `usedKilometers` (Phase 9.7.3) seeds totalKilometers.
   * Phase 11.3.2 (Bug 8) — `callsign` accepted (Funkrufname).
   * Phase 12.3.1 — `extraModuleIds` adds non-default modules at 100 % spec
   * cost (no surcharge), and `removedDefaultFamilies` strips listed default
   * families (no refund — they were "free" anyway).
   */
  buyVehicle: (
    stationId: string,
    type: VehicleType,
    opts?: {
      name?: string;
      callsign?: string;
      used?: boolean;
      usedKilometers?: number;
      extraModuleIds?: string[];
      removedDefaultFamilies?: string[];
    },
  ) => Vehicle | null;
  updateVehicle: (id: string, patch: Partial<Vehicle>) => void;
  removeVehicle: (id: string) => void;

  addMission: (mission: Mission) => void;
  updateMission: (id: string, patch: Partial<Mission>) => void;
  removeMission: (id: string) => void;

  dispatchVehiclesToMission: (missionId: string, vehicleIds: string[]) => boolean;

  pushToast: (toast: Omit<ToastMessage, 'id' | 'createdAt'>) => void;
  dismissToast: (id: string) => void;
  consumeMigrationToast: () => string | null;

  openMainPanel: (tab?: MainPanelTab) => void;
  closeMainPanel: () => void;
  setMainPanelTab: (tab: MainPanelTab) => void;
  selectDetail: (sel: DetailSelection) => void;
  clearDetail: () => void;
  toggleLayer: (key: keyof LayerToggles) => void;
  /** Phase 13.10 — Show / hide acquirable NPC stations on the map. */
  toggleShowAcquirableStations: () => void;
  toggleMute: () => void;

  setSandboxMissionInterval: (sec: number) => void;
  /** Phase 10.7.3 — share of police missions among generated spawns (0–100). */
  setSandboxPoliceShare: (pct: number) => void;

  // Phase 6.4 — UI prefs
  setAudioVolume: (volume: number) => void;
  setTimeFormat: (format: TimeFormat) => void;
  setReduceMotion: (on: boolean) => void;

  // Phase 6.1 — Werkstatt
  startRepair: (vehicleId: string, mode: RepairMode) => boolean;

  // Phase 6.3 — hydrants (transient cache, in-memory only)
  setHydrants: (hydrants: Hydrant[]) => void;
  /** Phase 13.7 — Cache of open-water features (rivers/lakes) for the home city. */
  setWaterFeatures: (features: import('@/data/waterFeatures').WaterFeature[]) => void;

  // Phase 7.2.4 — NPC stations (transient)
  setNpcStations: (npcs: NpcStation[]) => void;
  acquireNpcStation: (npcId: string) => Station | null;

  // Phase 7.2.5 — modules
  installModule: (vehicleId: string, moduleId: string) => boolean;
  uninstallModule: (vehicleId: string, instanceId: string) => boolean;

  // Phase 12.3 — Werkstatt-basierter Modul-Tausch.
  /** Start a workshop swap: pay surcharge cost, set vehicle to Status 6 for 5 min. */
  startModuleSwap: (
    vehicleId: string,
    addModuleIds: string[],
    removeInstanceIds: string[],
  ) => boolean;
  /** Cancel a running swap: 50 % cost refund, vehicle returns to Status 2. */
  cancelModuleSwap: (vehicleId: string) => boolean;

  // Phase 7.4.6 — onboarding
  completeOnboarding: () => void;

  // Phase 7.1.1 — slot management
  switchSlot: (slotId: number) => void;
  startNewSlotGame: (slotId: number, mode: GameMode) => void;

  // Phase 8.3.1 — loans
  takeLoan: (amount: number) => boolean;
  repayLoan: (loanId: string, amount: number) => boolean;

  // Phase 9.4 — Sprechwunsch / radio request handling
  acceptRadioRequest: (id: string) => boolean;
  acceptAllRadioRequests: () => number;
  dismissRadioRequest: (id: string) => void;

  // Phase 9.7.3 — Used vehicle market
  refreshUsedListings: (manual: boolean) => boolean;
  ensureUsedListings: () => void;

  // Phase 10.5.1 — Held-person management
  /** Move a held person from a station's cell directly to court (5000 € reward, 1× attempt). */
  transferHeldToCourt: (stationId: string, personId: string) => boolean;

  tick: (deltaMs: number) => void;
  resetGame: (mode?: GameMode) => void;
}

export type GameStore = GameState & GameActions;

const makeInitialState = (mode: GameMode = 'normal'): GameState => ({
  credits: STARTING_CREDITS[mode],
  dailyUpkeep: 0,
  points: 0,
  gameTime: Date.now(),
  // Phase 11.6 — Speed-Multiplier > 1 nur in Sandbox; Klassik / Realistic = 1×.
  speedMultiplier: mode === 'sandbox' ? 5 : 1,
  mode,
  paused: false,
  level: 1,
  homeCity: null,
  buildMode: null,
  stations: [],
  vehicles: [],
  missions: [],
  toasts: [],
  mapCenter: DEFAULT_CENTER,
  mapZoom: DEFAULT_ZOOM,
  uiPrefs: defaultUiPrefs(),
  // Phase 15.9 — Closed-Beta-Defaults für frische Slots.
  beta: {
    onboardingShown: false,
    testerPseudonym: `Tester-${Math.floor(Math.random() * 0xffff)
      .toString(16)
      .toUpperCase()
      .padStart(4, '0')}`,
  },
  sandboxConfig: defaultSandboxConfig(),
  mainPanelOpen: false,
  mainPanelTab: 'missions',
  selectedDetail: null,
  hydrants: [],
  waterFeatures: [],
  npcStations: [],
  stats: defaultStats(),
  activeSlotId: 1,
  loans: [],
  lastUpkeepTickAt: undefined,
  pauseUsage: undefined,
  radioRequests: [],
  usedVehicleListings: [],
  usedListingsRefreshedAt: undefined,
});

function computeDailyUpkeep(stations: Station[], vehicles: Vehicle[]): number {
  // Phase 8.2 — extension upkeep folded in via effectiveDailyUpkeep().
  const stationSum = stations.reduce((acc, s) => acc + effectiveDailyUpkeep(s), 0);
  const vehicleSum = vehicles.reduce((acc, v) => acc + VEHICLE_SPECS[v.type].upkeepPerDay, 0);
  return stationSum + vehicleSum;
}

function makeVehicle(stationId: string, type: VehicleType, position: Vehicle['position']): Vehicle {
  const spec = VEHICLE_SPECS[type];
  return {
    id: `veh_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    type,
    stationId,
    status: 2,
    position,
    waterTankCurrent: spec.waterTankMax,
    waterTankMax: spec.waterTankMax,
    breathingAirCurrent: spec.breathingAirMax,
    breathingAirMax: spec.breathingAirMax,
    condition: 1,
  };
}

function isStationReady(s: Station, gameTime: number): boolean {
  if (!s.buildCompleteAt) return true;
  return gameTime >= s.buildCompleteAt;
}

/**
 * Reset all animation/transit fields on a vehicle.
 *
 * Phase 9.7 — also accumulates `legDistanceKm` into `totalKilometers` and
 * applies a tiny condition wear of -0.0001 % per km (negligible until you've
 * piled up real mileage; matches the spec's "slow distance-driven wear").
 */
function clearTransitFields(v: Vehicle): Vehicle {
  const km = v.legDistanceKm ?? 0;
  const newTotal = (v.totalKilometers ?? 0) + km;
  const wear = km * 0.0001 / 100; // -0.001 % per km (very mild)
  const newCondition = Math.max(0, v.condition - wear);
  return {
    ...v,
    routeCoords: undefined,
    routeProgress: undefined,
    legStartedAt: undefined,
    legDurationMs: undefined,
    legDistanceKm: undefined,
    totalKilometers: newTotal,
    condition: newCondition,
  };
}

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ...makeInitialState('normal'),

      setMode: (mode) => {
        // Phase 12.1.2 (Bug N3) — When the slot is brand-new (no homeCity yet),
        // switching to Realistic must also reset credits to its 2 Mio € floor.
        // The store is always initialised with `mode: 'normal'` (3 Mio €), so
        // without this, Realistic slots inherited 3 M.
        const state = get();
        const isFreshSlot = state.homeCity === null;
        if (isFreshSlot) {
          set({
            mode,
            credits: STARTING_CREDITS[mode],
            speedMultiplier: mode === 'sandbox' ? 5 : 1,
          });
        } else {
          set({ mode });
        }
      },
      setSpeedMultiplier: (speedMultiplier) => {
        // Phase 11.6 — Klassik / Realistic laufen in Echtzeit; nur Sandbox darf
        // schneller als 1× ticken.
        const { mode } = get();
        const clamped: SpeedMultiplier = mode !== 'sandbox' && speedMultiplier > 1 ? 1 : speedMultiplier;
        set({ speedMultiplier: clamped });
      },
      togglePause: () => {
        // Phase 7.4.8 — Realistic mode now allows pause but with a points
        // penalty (-0.5 points per pause-second, real time).
        // Phase 9.6.1 — Pause-Budget: 2 freie pro Spieljahr, danach 50.000 € pro Pause.
        const state = get();
        if (!state.paused) {
          // ─── Pausing ───
          const consumeFromBudget = state.mode !== 'sandbox';
          let nextUsage = state.pauseUsage ?? {
            yearStartedAt: state.gameTime,
            freePausesUsed: 0,
            paidPausesCount: 0,
          };
          // Roll the year forward if needed (365 game-days = 365 * GAME_DAY_MS).
          const YEAR_MS = 365 * GAME_DAY_MS;
          if (state.gameTime - nextUsage.yearStartedAt >= YEAR_MS) {
            nextUsage = {
              yearStartedAt:
                nextUsage.yearStartedAt +
                Math.floor((state.gameTime - nextUsage.yearStartedAt) / YEAR_MS) * YEAR_MS,
              freePausesUsed: 0,
              paidPausesCount: nextUsage.paidPausesCount,
            };
          }
          if (consumeFromBudget) {
            const PAUSE_FEE = 50_000;
            if (nextUsage.freePausesUsed < 2) {
              nextUsage = { ...nextUsage, freePausesUsed: nextUsage.freePausesUsed + 1 };
              get().pushToast({
                kind: 'info',
                text: `Pause aktiviert (frei) – ${2 - nextUsage.freePausesUsed} kostenlose dieses Jahr übrig.`,
              });
            } else {
              if (state.credits < PAUSE_FEE) {
                get().pushToast({
                  kind: 'error',
                  text: `Pause kostet ${PAUSE_FEE.toLocaleString('de-DE')} € — nicht genug Credits.`,
                });
                return;
              }
              set({ credits: state.credits - PAUSE_FEE });
              nextUsage = { ...nextUsage, paidPausesCount: nextUsage.paidPausesCount + 1 };
              get().pushToast({
                kind: 'warning',
                text: `Kostenpflichtige Pause aktiviert: -${PAUSE_FEE.toLocaleString('de-DE')} €`,
              });
            }
          }
          set({
            paused: true,
            realisticPauseStartedAt:
              state.mode === 'realistic' ? Date.now() : undefined,
            pauseUsage: nextUsage,
          });
          if (state.mode === 'realistic') {
            get().pushToast({
              kind: 'warning',
              text: 'Pause aktiv — Realistisch-Modus berechnet Punkt-Abzug.',
            });
          }
          return;
        }
        // ─── Resuming ───
        let pointsDelta = 0;
        if (state.mode === 'realistic' && state.realisticPauseStartedAt !== undefined) {
          const realSec = (Date.now() - state.realisticPauseStartedAt) / 1000;
          pointsDelta = -Math.round(realSec * 0.5);
        }
        set((s) => ({
          paused: false,
          realisticPauseStartedAt: undefined,
          points: pointsDelta !== 0 ? s.points + pointsDelta : s.points,
        }));
        if (pointsDelta !== 0) {
          get().pushToast({
            kind: 'warning',
            text: `Pause-Strafe: ${pointsDelta} Punkte abgezogen.`,
          });
        }
      },
      setMapView: (center, zoom) => set({ mapCenter: center, mapZoom: zoom }),

      addCredits: (amount) => set((s) => ({ credits: s.credits + amount })),
      spendCredits: (amount) => {
        if (get().mode === 'sandbox') return true;
        const { credits } = get();
        if (credits < amount) return false;
        set({ credits: credits - amount });
        return true;
      },

      addPoints: (delta) => set((s) => ({ points: s.points + delta })),
      awardPoints: (action) => {
        const { mode } = get();
        const delta = pointsFor(action, mode);
        if (delta !== 0) set((s) => ({ points: s.points + delta }));
      },

      setHomeCity: (city) => {
        // Phase 10.5 — Synthetic courthouse near city center, ±0.005° offset.
        const courthousePosition = {
          lat: city.center.lat + (Math.random() - 0.5) * 0.01,
          lng: city.center.lng + (Math.random() - 0.5) * 0.01,
        };
        set({
          homeCity: city,
          mapCenter: city.center,
          mapZoom: DEFAULT_ZOOM,
          courthousePosition,
        });
        // Phase 7.1.1 — slot just became occupied; seed the meta-index.
        const s = get();
        writeSlotMeta({
          id: s.activeSlotId,
          cityName: city.displayName,
          mode: s.mode,
          level: s.level,
          credits: s.credits,
          lastPlayed: Date.now(),
        });
      },

      buyLevelUp: () => {
        const { level, mode } = get();
        const clamped = clampLevel(level);
        const cost = getLevelUpCost(clamped);
        if (!Number.isFinite(cost)) return false;
        if (mode !== 'sandbox') {
          const { credits } = get();
          if (credits < cost) return false;
          set({ credits: credits - cost });
        }
        set({ level: clamped + 1 });
        return true;
      },

      setBuildMode: (buildMode) => set({ buildMode }),

      buildStation: ({ kind, name, position }) => {
        const { mode, stations, vehicles, gameTime } = get();
        const tier = getCurrentTier(kind, 1);
        const cost = tier.cost;

        if (mode !== 'sandbox') {
          const { credits } = get();
          if (credits < cost) return null;
          set({ credits: credits - cost });
        }

        const buildMs = getEffectiveBuildTimeMs(tier.buildTimeHours, mode);
        const station: Station = {
          id: `stn_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          name,
          kind,
          position,
          vehicleIds: [],
          level: 1,
          buildStartedAt: buildMs > 0 ? gameTime : undefined,
          buildCompleteAt: buildMs > 0 ? gameTime + buildMs : undefined,
          currentPatients: kind === 'hospital' ? [] : undefined,
          // Phase 9.5 — Bookkeeping for sale refund.
          totalSpent: mode === 'sandbox' ? 0 : cost,
        };
        const nextStations = [...stations, station];
        set((s) => ({
          stations: nextStations,
          dailyUpkeep: computeDailyUpkeep(nextStations, vehicles),
          stats: {
            ...s.stats,
            spentOnStations:
              s.stats.spentOnStations + (mode === 'sandbox' ? 0 : cost),
          },
        }));

        const delta = pointsFor('newStation', mode);
        if (delta !== 0) set((s) => ({ points: s.points + delta }));

        return station;
      },

      upgradeStation: (stationId) => {
        const { stations, vehicles, mode, gameTime } = get();
        const st = stations.find((s) => s.id === stationId);
        if (!st) return false;
        const next = getNextTier(st.kind, st.level);
        if (!next) return false;

        if (mode !== 'sandbox') {
          const { credits } = get();
          if (credits < next.cost) return false;
          set({ credits: credits - next.cost });
        }

        const buildMs = getEffectiveBuildTimeMs(next.buildTimeHours, mode);
        const updated = stations.map((s) =>
          s.id === stationId
            ? {
                ...s,
                level: s.level + 1,
                buildStartedAt: buildMs > 0 ? gameTime : undefined,
                buildCompleteAt: buildMs > 0 ? gameTime + buildMs : undefined,
                totalSpent:
                  (s.totalSpent ?? 0) + (mode === 'sandbox' ? 0 : next.cost),
              }
            : s,
        );
        set((s) => ({
          stations: updated,
          dailyUpkeep: computeDailyUpkeep(updated, vehicles),
          stats: {
            ...s.stats,
            spentOnStations:
              s.stats.spentOnStations + (mode === 'sandbox' ? 0 : next.cost),
          },
        }));
        return true;
      },

      buildStationExtension: (stationId, extensionId) => {
        const state = get();
        const station = state.stations.find((s) => s.id === stationId);
        if (!station) return false;
        const ext = extensionById(extensionId);
        if (!ext) return false;
        const installed = (station.extensions ?? []).map((e) => e.extensionId);
        if (installed.includes(extensionId)) return false;

        if (state.mode !== 'sandbox') {
          if (state.credits < ext.cost) return false;
          set({ credits: state.credits - ext.cost });
        }

        const buildMs = getEffectiveBuildTimeMs(ext.buildTimeHours, state.mode);
        const inst = {
          id: `ext_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          extensionId,
          installedAt: state.gameTime,
          buildStartedAt: buildMs > 0 ? state.gameTime : undefined,
          buildCompleteAt: buildMs > 0 ? state.gameTime + buildMs : undefined,
        };
        set((s) => {
          const updated = s.stations.map((st) =>
            st.id === stationId
              ? {
                  ...st,
                  extensions: [...(st.extensions ?? []), inst],
                  totalSpent:
                    (st.totalSpent ?? 0) + (state.mode === 'sandbox' ? 0 : ext.cost),
                }
              : st,
          );
          return {
            stations: updated,
            dailyUpkeep: computeDailyUpkeep(updated, s.vehicles),
            stats: {
              ...s.stats,
              spentOnStations:
                s.stats.spentOnStations + (state.mode === 'sandbox' ? 0 : ext.cost),
            },
          };
        });
        return true;
      },

      recomputeUpkeep: () => {
        const { stations, vehicles } = get();
        set({ dailyUpkeep: computeDailyUpkeep(stations, vehicles) });
      },

      // Phase 8.3.1 — Kredit aufnehmen.
      takeLoan: (amount) => {
        const state = get();
        if (state.mode === 'sandbox') return false;
        if (!Number.isFinite(amount) || amount < LOAN_MIN_AMOUNT) return false;
        if (amount > LOAN_MAX_AMOUNT) return false;
        const cap = availableLoanCapacity(state.loans, state.dailyUpkeep);
        if (amount > cap) return false;

        const loan: Loan = {
          id: `loan_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          principalAmount: amount,
          remainingPrincipal: amount,
          interestRatePerWeek: LOAN_INTEREST_RATE_WEEKLY,
          takenAtGameTime: state.gameTime,
          lastInterestAt: state.gameTime,
          accruedInterest: 0,
        };
        set({
          loans: [...state.loans, loan],
          credits: state.credits + amount,
        });
        return true;
      },

      // Phase 9.4 — Sprechwunsch annehmen: dispatch implizites Folgefahrzeug.
      acceptRadioRequest: (id) => {
        const state = get();
        const req = (state.radioRequests ?? []).find((r) => r.id === id);
        if (!req) return false;

        // Auto-disposition based on request kind. Picks nearest free vehicle.
        let neededType: VehicleType | VehicleType[] | null = null;
        if (req.kind === 'request-nef') neededType = 'NEF';
        else if (req.kind === 'request-dlk') neededType = 'DLK';
        else if (req.kind === 'request-water') neededType = ['TLF', 'SW'];

        if (neededType) {
          const types = Array.isArray(neededType) ? neededType : [neededType];
          // Free vehicle of the right type, ordered by distance to mission.
          const mission = state.missions.find((m) => m.id === req.missionId);
          if (!mission) {
            // Mission gone; just mark accepted.
            set({
              radioRequests: (state.radioRequests ?? []).map((r) =>
                r.id === id ? { ...r, acceptedAt: state.gameTime } : r,
              ),
            });
            return true;
          }
          const candidates = state.vehicles
            .filter(
              (v) =>
                types.includes(v.type) &&
                (v.status === 1 || v.status === 2) &&
                !v.assignedMissionId &&
                !v.alarmStartedAt &&
                v.repairStartedAt === undefined &&
                v.condition > 0.25,
            )
            .map((v) => {
              const stn = state.stations.find((s) => s.id === v.stationId);
              const from = stn?.position ?? v.position;
              const dx = from.lat - mission.position.lat;
              const dy = from.lng - mission.position.lng;
              return { v, dist: dx * dx + dy * dy };
            })
            .sort((a, b) => a.dist - b.dist);
          if (candidates.length === 0) {
            // Still mark accepted, but warn the player.
            get().pushToast({
              kind: 'warning',
              text:
                req.kind === 'request-nef'
                  ? 'Kein NEF verfügbar — manuell disponieren.'
                  : req.kind === 'request-dlk'
                    ? 'Keine Drehleiter verfügbar — manuell disponieren.'
                    : 'Kein TLF/SW verfügbar — manuell disponieren.',
            });
            set({
              radioRequests: (state.radioRequests ?? []).map((r) =>
                r.id === id ? { ...r, acceptedAt: state.gameTime } : r,
              ),
            });
            return true;
          }
          const ok = get().dispatchVehiclesToMission(req.missionId, [candidates[0].v.id]);
          if (ok) {
            get().pushToast({
              kind: 'success',
              text:
                req.kind === 'request-nef'
                  ? 'NEF wird automatisch nachalarmiert.'
                  : req.kind === 'request-dlk'
                    ? 'Drehleiter wird automatisch nachalarmiert.'
                    : 'TLF/SW wird automatisch nachalarmiert.',
            });
          }
        }

        set((s) => ({
          radioRequests: (s.radioRequests ?? []).map((r) =>
            r.id === id ? { ...r, acceptedAt: s.gameTime } : r,
          ),
        }));
        return true;
      },

      acceptAllRadioRequests: () => {
        const ids = (get().radioRequests ?? [])
          .filter((r) => !r.acceptedAt)
          .map((r) => r.id);
        let n = 0;
        for (const id of ids) {
          if (get().acceptRadioRequest(id)) n++;
        }
        return n;
      },

      dismissRadioRequest: (id) => {
        set((s) => ({
          radioRequests: (s.radioRequests ?? []).filter((r) => r.id !== id),
        }));
      },

      // Phase 9.7.3 — Used market
      refreshUsedListings: (manual) => {
        const state = get();
        if (manual && state.mode !== 'sandbox') {
          if (state.credits < USED_REFRESH_FEE) return false;
          set({ credits: state.credits - USED_REFRESH_FEE });
        }
        const listings = generateUsedListings(state.gameTime);
        set({
          usedVehicleListings: listings,
          usedListingsRefreshedAt: state.gameTime,
        });
        return true;
      },
      // Phase 10.5.1 — Held-Person zur Justiz überstellen.
      // Simple model: instant transfer once a free FuStW/GruKW is available
      // at the station; the vehicle takes a "round-trip" to courthouse via a
      // status-3 dispatch. We approximate by adding the reward immediately.
      transferHeldToCourt: (stationId, personId) => {
        const state = get();
        const station = state.stations.find((s) => s.id === stationId);
        if (!station) return false;
        const held = (station.heldPersons ?? []).find((p) => p.id === personId);
        if (!held) return false;
        // Phase 11.1.6 (Bug 4) — Reward immer 5 000 € außer Sandbox.
        // Phase 12.1.4 — Reward zählt IMMER zu stats.totalIncome, auch in
        // Sandbox (wo Credits durch das große Wallet effektiv "gedeckelt"
        // sind), damit das Statistik-Tracking konsistent bleibt.
        const reward = state.mode === 'sandbox' ? 0 : 5_000;
        const trackedReward = 5_000; // logical reward for stats tracking
        set({
          stations: state.stations.map((s) =>
            s.id === stationId
              ? {
                  ...s,
                  heldPersons: (s.heldPersons ?? []).filter((p) => p.id !== personId),
                }
              : s,
          ),
          credits: state.credits + reward,
          stats: {
            ...state.stats,
            totalIncome: state.stats.totalIncome + trackedReward,
            byOrganization: {
              fire: state.stats.byOrganization?.fire ?? defaultOrgStats(),
              ems: state.stats.byOrganization?.ems ?? defaultOrgStats(),
              police: {
                ...(state.stats.byOrganization?.police ?? defaultOrgStats()),
                arrestsCompleted:
                  (state.stats.byOrganization?.police?.arrestsCompleted ?? 0) + 1,
              },
            },
          },
        });
        get().pushToast({
          kind: 'success',
          text:
            reward > 0
              ? `${held.name} an Gericht überstellt (+${reward.toLocaleString('de-DE')} €).`
              : `${held.name} an Gericht überstellt (+${trackedReward.toLocaleString('de-DE')} € — Sandbox).`,
        });
        return true;
      },

      ensureUsedListings: () => {
        const state = get();
        const lastAt = state.usedListingsRefreshedAt;
        const due =
          lastAt === undefined ||
          state.gameTime - lastAt >= USED_REFRESH_INTERVAL_MS;
        if (due || (state.usedVehicleListings ?? []).length === 0) {
          set({
            usedVehicleListings: generateUsedListings(state.gameTime),
            usedListingsRefreshedAt: state.gameTime,
          });
        }
      },

      repayLoan: (loanId, amount) => {
        const state = get();
        if (state.mode === 'sandbox') return false;
        if (!Number.isFinite(amount) || amount <= 0) return false;
        const loan = state.loans.find((l) => l.id === loanId);
        if (!loan) return false;
        const pay = Math.min(amount, loan.remainingPrincipal, state.credits);
        if (pay <= 0) return false;
        const newRemaining = loan.remainingPrincipal - pay;
        const updated: Loan[] =
          newRemaining <= 0
            ? state.loans.filter((l) => l.id !== loanId)
            : state.loans.map((l) =>
                l.id === loanId ? { ...l, remainingPrincipal: newRemaining } : l,
              );
        set({ loans: updated, credits: state.credits - pay });
        return true;
      },

      removeStation: (stationId) =>
        set((s) => {
          const stations = s.stations.filter((st) => st.id !== stationId);
          const vehicles = s.vehicles.filter((v) => v.stationId !== stationId);
          return {
            stations,
            vehicles,
            dailyUpkeep: computeDailyUpkeep(stations, vehicles),
            selectedDetail:
              s.selectedDetail &&
              s.selectedDetail.kind === 'station' &&
              s.selectedDetail.id === stationId
                ? null
                : s.selectedDetail,
          };
        }),

      // Phase 9.5 — Sale (with vehicles + extensions).
      sellStation: (stationId) => {
        const state = get();
        const station = state.stations.find((s) => s.id === stationId);
        if (!station) return 0;

        // Compute refund.
        // Phase 11.5 — Refund covers vehicle base cost + 50 % of installed
        // (non-default) module costs. Default modules came "free" with the
        // vehicle and are not refundable beyond the chassis price.
        const stationVehicles = state.vehicles.filter((v) => v.stationId === stationId);
        const vehicleSum = stationVehicles.reduce((acc, v) => {
          const spec = VEHICLE_SPECS[v.type];
          let total = Math.round(spec.cost * 0.5);
          for (const inst of v.installedModules ?? []) {
            if (inst.isDefault) continue;
            const m = moduleById(inst.moduleId);
            if (m) total += Math.round(m.cost * 0.5);
          }
          return acc + total;
        }, 0);
        const stationSpent = station.totalSpent ?? 0;
        // Note: `buildStartedAt === 0` is a valid timestamp; check undefined explicitly.
        const isUnderConstruction =
          station.buildStartedAt !== undefined &&
          station.buildCompleteAt !== undefined &&
          state.gameTime < station.buildCompleteAt;
        const stationRefund = Math.round(
          stationSpent * (isUnderConstruction ? 0.8 : 0.5),
        );
        const refund = state.mode === 'sandbox' ? 0 : stationRefund + vehicleSum;

        // Strip station + its vehicles, fail-or-strip-vehicles in active missions.
        const stationVehicleIds = new Set(stationVehicles.map((v) => v.id));
        const updatedVehicles = state.vehicles.filter((v) => v.stationId !== stationId);
        const updatedStations = state.stations.filter((s) => s.id !== stationId);
        const updatedMissions = state.missions.map((m) => {
          if (m.status === 'closed' || m.status === 'failed') return m;
          const remainingAssigned = m.assignedVehicleIds.filter(
            (vid) => !stationVehicleIds.has(vid),
          );
          if (remainingAssigned.length === m.assignedVehicleIds.length) return m;
          // No more assigned vehicles → mission fails.
          if (remainingAssigned.length === 0 && m.status !== 'open') {
            return {
              ...m,
              status: 'failed' as const,
              closedAt: state.gameTime,
              phase: 'closing' as const,
              assignedVehicleIds: [],
            };
          }
          return { ...m, assignedVehicleIds: remainingAssigned };
        });

        // Phase 12.1.5 — Refund-Berechnung in stats.totalIncome reflektieren,
        // auch in Sandbox (logisch korrekt, selbst wenn refund=0 tatsächlich
        // ausgezahlt wird).
        const trackedRefund = stationRefund + vehicleSum;
        set({
          stations: updatedStations,
          vehicles: updatedVehicles,
          missions: updatedMissions,
          credits: state.credits + refund,
          dailyUpkeep: computeDailyUpkeep(updatedStations, updatedVehicles),
          stats: {
            ...state.stats,
            totalIncome: state.stats.totalIncome + trackedRefund,
          },
          selectedDetail:
            state.selectedDetail &&
            state.selectedDetail.kind === 'station' &&
            state.selectedDetail.id === stationId
              ? null
              : state.selectedDetail,
        });

        // The numeric return is what the UI actually paid out — for Sandbox
        // this is 0; for the rest it's `stationRefund + vehicleSum`. The
        // ConfirmDialog shows `trackedRefund` ahead of this call, so the
        // sticker value the user sees is consistent with reality.
        return refund;
      },

      addVehicle: (vehicle) =>
        set((s) => {
          const vehicles = [...s.vehicles, vehicle];
          const stations = s.stations.map((st) =>
            st.id === vehicle.stationId
              ? { ...st, vehicleIds: [...st.vehicleIds, vehicle.id] }
              : st,
          );
          return {
            vehicles,
            stations,
            dailyUpkeep: computeDailyUpkeep(stations, vehicles),
          };
        }),

      buyVehicle: (stationId, type, opts) => {
        const { stations, mode } = get();
        const station = stations.find((s) => s.id === stationId);
        if (!station) return null;
        const spec = VEHICLE_SPECS[type];

        // Phase 8.2.2 — Combined gating:
        //  - EMS vehicles need ems-station OR a fire station with Rettungswachenanbau.
        //  - Other categories follow the spec's allowedStations as before.
        if (spec.category === 'ems') {
          if (!stationAllowsEmsVehicles(station)) return null;
        } else {
          if (!spec.allowedStations.includes(station.kind)) return null;
        }
        // Phase 9.6.5 — Vehicles like FWK require a minimum tier.
        if (spec.minStationTier && station.level < spec.minStationTier) return null;

        // Phase 8.5 — Used-vehicle pricing (Gebraucht tab) costs 60 %.
        const used = opts?.used === true;
        const baseCost = used ? Math.round(spec.cost * 0.6) : spec.cost;

        // Phase 12.3.1 — Pre-purchase modules cost 100 % spec cost (no surcharge).
        const extraIds = opts?.extraModuleIds ?? [];
        let extraCost = 0;
        for (const id of extraIds) {
          const m = moduleById(id);
          if (!m) return null;
          extraCost += m.cost;
        }
        const effectiveCost = baseCost + extraCost;

        if (mode !== 'sandbox') {
          const { credits } = get();
          if (credits < effectiveCost) return null;
          set({ credits: credits - effectiveCost });
        }

        // Phase 9.3 — Standard-Module ab Werk installieren (im Grundpreis enthalten).
        // Phase 12.3.1 — gewisse Default-Familien werden via removedDefaultFamilies entfernt.
        const removedFamilies = new Set(opts?.removedDefaultFamilies ?? []);
        const defaultInstalls: VehicleModuleInstance[] = (spec.defaultModules ?? [])
          .filter((dm) => !removedFamilies.has(dm.family))
          .map((dm, idx) => ({
            id: `vmod_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 5)}`,
            moduleId: dm.size ? `${dm.family}:${dm.size}` : dm.family,
            installedAt: get().gameTime,
            isDefault: true,
          }));
        const extraInstalls: VehicleModuleInstance[] = extraIds.map((mid, idx) => ({
          id: `vmod_extra_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 5)}`,
          moduleId: mid,
          installedAt: get().gameTime,
          isDefault: false,
        }));

        const vehicle: Vehicle = {
          ...makeVehicle(stationId, type, station.position),
          ...(opts?.name ? { displayName: opts.name } : {}),
          ...(opts?.callsign ? { callsign: opts.callsign } : {}),
          ...(used ? { used: true, condition: 0.6 } : {}),
          installedModules: [...defaultInstalls, ...extraInstalls],
          totalKilometers: opts?.usedKilometers ?? 0,
        };
        set((s) => {
          const vehicles = [...s.vehicles, vehicle];
          const updatedStations = s.stations.map((st) =>
            st.id === stationId ? { ...st, vehicleIds: [...st.vehicleIds, vehicle.id] } : st,
          );
          return {
            vehicles,
            stations: updatedStations,
            dailyUpkeep: computeDailyUpkeep(updatedStations, vehicles),
            stats: {
              ...s.stats,
              spentOnVehicles:
                s.stats.spentOnVehicles + (mode === 'sandbox' ? 0 : baseCost),
              spentOnModules:
                s.stats.spentOnModules + (mode === 'sandbox' ? 0 : extraCost),
            },
          };
        });

        const delta = pointsFor('newVehicle', mode);
        if (delta !== 0) set((s) => ({ points: s.points + delta }));

        return vehicle;
      },

      updateVehicle: (id, patch) =>
        set((s) => ({
          vehicles: s.vehicles.map((v) => (v.id === id ? { ...v, ...patch } : v)),
        })),

      removeVehicle: (id) =>
        set((s) => {
          const vehicles = s.vehicles.filter((v) => v.id !== id);
          const stations = s.stations.map((st) => ({
            ...st,
            vehicleIds: st.vehicleIds.filter((vid) => vid !== id),
          }));
          return {
            vehicles,
            stations,
            dailyUpkeep: computeDailyUpkeep(stations, vehicles),
            selectedDetail:
              s.selectedDetail && s.selectedDetail.kind === 'vehicle' && s.selectedDetail.id === id
                ? null
                : s.selectedDetail,
          };
        }),

      addMission: (mission) => set((s) => ({ missions: [...s.missions, mission] })),
      updateMission: (id, patch) =>
        set((s) => ({
          missions: s.missions.map((m) => (m.id === id ? { ...m, ...patch } : m)),
        })),
      removeMission: (id) =>
        set((s) => ({
          missions: s.missions.filter((m) => m.id !== id),
          selectedDetail:
            s.selectedDetail && s.selectedDetail.kind === 'mission' && s.selectedDetail.id === id
              ? null
              : s.selectedDetail,
        })),

      dispatchVehiclesToMission: (missionId, vehicleIds) => {
        const state = get();
        const mission = state.missions.find((m) => m.id === missionId);
        if (!mission) return false;
        if (vehicleIds.length === 0) return false;

        // Reject vehicles that are condition-critical (Phase 6.1 repair gate).
        const undispatchable = vehicleIds.filter((id) => {
          const v = state.vehicles.find((x) => x.id === id);
          if (!v) return true;
          return v.condition <= REPAIR_DISABLE_THRESHOLD || v.repairStartedAt !== undefined;
        });
        if (undispatchable.length > 0) return false;

        const stationsById = new Map(state.stations.map((s) => [s.id, s]));
        const now = state.gameTime;

        const updatedVehicles = state.vehicles.map((v) => {
          if (!vehicleIds.includes(v.id)) return v;
          const station = stationsById.get(v.stationId);
          if (!station) return v;
          const alarmDur = getAlarmDurationMs(station.kind);
          return {
            ...v,
            assignedMissionId: missionId,
            destination: 'mission' as const,
            alarmStartedAt: now,
            alarmCompleteAt: now + alarmDur,
            // Animation fields will be filled by useVehicleRouting after fetch.
            routeCoords: undefined,
            routeProgress: undefined,
            legStartedAt: undefined,
            legDurationMs: undefined,
          };
        });

        const updatedMissions = state.missions.map((m) =>
          m.id === missionId
            ? {
                ...m,
                status: m.status === 'open' ? ('dispatched' as const) : m.status,
                dispatchedAt: m.dispatchedAt ?? now,
                assignedVehicleIds: Array.from(new Set([...m.assignedVehicleIds, ...vehicleIds])),
                // Phase 8.4 — phase advances to 'enroute' as soon as vehicles
                // are dispatched (unless already further along).
                phase:
                  m.phase === 'reconnaissance' ||
                  m.phase === 'engagement' ||
                  m.phase === 'closing'
                    ? m.phase
                    : ('enroute' as const),
              }
            : m,
        );

        set({ vehicles: updatedVehicles, missions: updatedMissions });
        return true;
      },

      pushToast: (toast) =>
        set((s) => ({
          toasts: [
            ...s.toasts,
            {
              ...toast,
              id: `toast_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              createdAt: Date.now(),
            },
          ],
        })),
      dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
      consumeMigrationToast: () => {
        const text = get().pendingMigrationToast ?? null;
        if (text) set({ pendingMigrationToast: undefined });
        return text;
      },

      openMainPanel: (tab) =>
        set((s) => ({
          mainPanelOpen: true,
          mainPanelTab: tab ?? s.mainPanelTab,
        })),
      closeMainPanel: () => set({ mainPanelOpen: false }),
      setMainPanelTab: (mainPanelTab) => set({ mainPanelTab }),
      selectDetail: (selectedDetail) => set({ selectedDetail }),
      clearDetail: () => set({ selectedDetail: null }),
      toggleLayer: (key) =>
        set((s) => ({
          uiPrefs: {
            ...s.uiPrefs,
            layerToggles: {
              ...s.uiPrefs.layerToggles,
              [key]: !s.uiPrefs.layerToggles[key],
            },
          },
        })),
      toggleShowAcquirableStations: () =>
        set((s) => ({
          uiPrefs: {
            ...s.uiPrefs,
            showAcquirableStations: !s.uiPrefs.showAcquirableStations,
          },
        })),
      toggleMute: () =>
        set((s) => ({
          uiPrefs: { ...s.uiPrefs, muted: !s.uiPrefs.muted },
        })),

      setSandboxMissionInterval: (sec) => {
        const clamped = Math.max(
          SANDBOX_INTERVAL_MIN_SEC,
          Math.min(SANDBOX_INTERVAL_MAX_SEC, Math.round(sec)),
        );
        set((s) => ({
          sandboxConfig: { ...s.sandboxConfig, missionIntervalSec: clamped },
        }));
      },
      setSandboxPoliceShare: (pct) => {
        const clamped = Math.max(0, Math.min(100, Math.round(pct)));
        set((s) => ({
          sandboxConfig: { ...s.sandboxConfig, policeMissionSharePct: clamped },
        }));
      },

      setAudioVolume: (volume) => {
        const clamped = Math.max(0, Math.min(1, volume));
        set((s) => ({ uiPrefs: { ...s.uiPrefs, audioVolume: clamped } }));
      },
      setTimeFormat: (timeFormat) =>
        set((s) => ({ uiPrefs: { ...s.uiPrefs, timeFormat } })),
      setReduceMotion: (reduceMotion) =>
        set((s) => ({ uiPrefs: { ...s.uiPrefs, reduceMotion } })),

      setHydrants: (hydrants) => set({ hydrants }),
      setWaterFeatures: (features) => set({ waterFeatures: features }),

      setNpcStations: (npcs) => set({ npcStations: npcs }),

      acquireNpcStation: (npcId) => {
        const state = get();
        const npc = state.npcStations.find((n) => n.id === npcId);
        if (!npc) return null;

        const cost = npc.acquireCost;
        if (state.mode !== 'sandbox') {
          if (state.credits < cost) return null;
          set({ credits: state.credits - cost });
        }

        const station: Station = {
          id: `stn_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          name: npc.name,
          kind: npc.kind,
          position: npc.position,
          vehicleIds: [],
          level: 1,
          buildStartedAt: undefined,
          buildCompleteAt: undefined,
          currentPatients: npc.kind === 'hospital' ? [] : undefined,
          // Phase 9.6.3 — übernehme Adresse + realName.
          realName: npc.name,
          address: npc.address,
          // Phase 9.5 — Bookkeeping for sale refund (Übernahme zählt als Spend).
          totalSpent: state.mode === 'sandbox' ? 0 : cost,
        };

        set((s) => ({
          stations: [...s.stations, station],
          npcStations: s.npcStations.filter((n) => n.id !== npcId),
          dailyUpkeep: computeDailyUpkeep([...s.stations, station], s.vehicles),
          stats: {
            ...s.stats,
            spentOnNpcAcquisition: s.stats.spentOnNpcAcquisition + (state.mode === 'sandbox' ? 0 : cost),
          },
        }));

        const delta = pointsFor('newStation', state.mode);
        if (delta !== 0) set((s) => ({ points: s.points + delta }));

        return station;
      },

      installModule: (vehicleId, moduleId) => {
        const state = get();
        const vehicle = state.vehicles.find((v) => v.id === vehicleId);
        if (!vehicle) return false;
        if (vehicle.status !== 2) return false; // must be at base
        const m = moduleById(moduleId);
        if (!m) return false;
        if (!m.compatibleTypes.includes(vehicle.type)) return false;

        const installed = vehicle.installedModules ?? [];
        const usedSlots = installed.reduce((acc, inst) => {
          const im = moduleById(inst.moduleId);
          return acc + (im?.slots ?? 0);
        }, 0);
        const spec = VEHICLE_SPECS[vehicle.type];
        if (usedSlots + m.slots > spec.moduleSlots) return false;

        if (state.mode !== 'sandbox') {
          if (state.credits < m.cost) return false;
          set({ credits: state.credits - m.cost });
        }

        const inst = {
          id: `mod_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          moduleId,
          installedAt: state.gameTime,
        };
        set((s) => ({
          vehicles: s.vehicles.map((v) =>
            v.id === vehicleId
              ? { ...v, installedModules: [...(v.installedModules ?? []), inst] }
              : v,
          ),
          stats: {
            ...s.stats,
            spentOnModules:
              s.stats.spentOnModules + (state.mode === 'sandbox' ? 0 : m.cost),
          },
        }));
        return true;
      },

      uninstallModule: (vehicleId, instanceId) => {
        const state = get();
        const vehicle = state.vehicles.find((v) => v.id === vehicleId);
        if (!vehicle) return false;
        if (vehicle.status !== 2) return false;
        const inst = (vehicle.installedModules ?? []).find((i) => i.id === instanceId);
        if (!inst) return false;
        const m = moduleById(inst.moduleId);
        if (!m) return false;
        // Phase 9.3.3 — Standard-Module geben nur 30 % zurück (sind im Grundpreis enthalten),
        // nachgekaufte Module geben 50 % zurück.
        const refundRate = inst.isDefault ? 0.3 : 0.5;
        if (state.mode !== 'sandbox') {
          set({ credits: state.credits + Math.round(m.cost * refundRate) });
        }
        set((s) => ({
          vehicles: s.vehicles.map((v) =>
            v.id === vehicleId
              ? {
                  ...v,
                  installedModules: (v.installedModules ?? []).filter(
                    (i) => i.id !== instanceId,
                  ),
                }
              : v,
          ),
        }));
        return true;
      },

      // Phase 12.3 — Module workshop swap.
      startModuleSwap: (vehicleId, addModuleIds, removeInstanceIds) => {
        const state = get();
        const vehicle = state.vehicles.find((v) => v.id === vehicleId);
        if (!vehicle) return false;
        // Vehicle must be at base, not already in workshop / repair / mission.
        if (vehicle.status !== 2) return false;
        if (vehicle.workshopUntilAt) return false;
        const station = state.stations.find((s) => s.id === vehicle.stationId);
        if (!station) return false;
        // Workshop access (Phase 11.2.4 N7 helper).
        if (!stationAllowsRepair(station)) return false;

        // Compute cost for swap.
        let totalCost = 0;
        // Add modules: spec.cost * 1.5
        for (const id of addModuleIds) {
          const m = moduleById(id);
          if (!m) return false;
          totalCost += Math.round(m.cost * (1 + MODULE_SWAP_SURCHARGE));
        }
        // Remove modules: refund 30 % for non-default, 0 % for default.
        let removeRefund = 0;
        for (const instId of removeInstanceIds) {
          const inst = (vehicle.installedModules ?? []).find((i) => i.id === instId);
          if (!inst) continue;
          if (inst.isDefault) continue;
          const m = moduleById(inst.moduleId);
          if (m) removeRefund += Math.round(m.cost * MODULE_SWAP_REFUND_RATE);
        }
        const netCost = Math.max(0, totalCost - removeRefund);
        if (state.mode !== 'sandbox' && state.credits < netCost) return false;

        const now = state.gameTime;
        const swap = {
          startedAt: now,
          removeInstanceIds: [...removeInstanceIds],
          addModuleIds: [...addModuleIds],
          totalCost: netCost,
        };
        set({
          credits: state.mode === 'sandbox' ? state.credits : state.credits - netCost,
          vehicles: state.vehicles.map((v) =>
            v.id === vehicleId
              ? {
                  ...v,
                  status: 6 as VehicleStatus,
                  workshopUntilAt: now + MODULE_SWAP_DURATION_MS,
                  pendingModuleSwap: swap,
                }
              : v,
          ),
          stats: {
            ...state.stats,
            spentOnModules: state.stats.spentOnModules + netCost,
          },
        });
        return true;
      },

      cancelModuleSwap: (vehicleId) => {
        const state = get();
        const vehicle = state.vehicles.find((v) => v.id === vehicleId);
        if (!vehicle || !vehicle.pendingModuleSwap) return false;
        const swap = vehicle.pendingModuleSwap;
        const refund =
          state.mode === 'sandbox'
            ? 0
            : Math.round(swap.totalCost * MODULE_SWAP_CANCEL_REFUND_RATE);
        set({
          credits: state.credits + refund,
          vehicles: state.vehicles.map((v) =>
            v.id === vehicleId
              ? {
                  ...v,
                  status: 2 as VehicleStatus,
                  workshopUntilAt: undefined,
                  pendingModuleSwap: undefined,
                }
              : v,
          ),
        });
        get().pushToast({
          kind: 'info',
          text:
            state.mode === 'sandbox'
              ? 'Modul-Tausch abgebrochen.'
              : `Modul-Tausch abgebrochen — ${refund.toLocaleString('de-DE')} € Refund.`,
        });
        return true;
      },

      completeOnboarding: () =>
        set((s) => ({ uiPrefs: { ...s.uiPrefs, onboardingCompleted: true } })),

      switchSlot: (slotId) => {
        // Persist the current slot pointer; the store rehydrates from the new
        // slot on next reload. For an in-place switch we'd need to manually
        // re-hydrate — we just write the pointer + reload here is not feasible
        // in this prototype, so the SaveBrowser handles it via a reload.
        writeActiveSlotId(slotId);
        set({ activeSlotId: slotId });
      },

      startNewSlotGame: (slotId, mode) => {
        writeActiveSlotId(slotId);
        // Wipe the slot before initialising a fresh state.
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            window.localStorage.removeItem(slotKey(slotId));
          } catch {
            // ignore
          }
        }
        const fresh = makeInitialState(mode);
        set({ ...fresh, activeSlotId: slotId });
      },

      startRepair: (vehicleId, mode) => {
        const state = get();
        const v = state.vehicles.find((x) => x.id === vehicleId);
        if (!v) return false;
        if (v.repairStartedAt !== undefined) return false; // already in repair
        // Cannot repair while in mission/transit — vehicle must be at base.
        if (v.status !== 2) return false;

        const cost = repairCostFor(v, mode);
        if (state.mode !== 'sandbox') {
          if (state.credits < cost) return false;
          set({ credits: state.credits - cost });
        }

        const dur = repairDurationMs(mode);
        set((s) => ({
          vehicles: s.vehicles.map((x) =>
            x.id === vehicleId
              ? {
                  ...x,
                  status: 6 as VehicleStatus,
                  repairStartedAt: s.gameTime,
                  repairCompleteAt: s.gameTime + dur,
                }
              : x,
          ),
          stats: {
            ...s.stats,
            spentOnRepairs:
              s.stats.spentOnRepairs + (state.mode === 'sandbox' ? 0 : cost),
          },
        }));
        return true;
      },

      tick: (deltaMs) => {
        const state = get();
        if (state.paused) return;

        const dtGame = deltaMs * state.speedMultiplier;
        const nextGameTime = state.gameTime + dtGame;

        // Working copies — only commit at the end.
        let stations = state.stations;
        let vehicles = state.vehicles;
        let missions = state.missions;
        let nextMissionAt = state.nextMissionAt;

        const stationsById = new Map(stations.map((s) => [s.id, s]));
        const stationsByIdMutable = new Map<string, Station>();

        // Side-effect collectors
        const toastsToPush: Array<Omit<ToastMessage, 'id' | 'createdAt'>> = [];
        let creditsDelta = 0;
        let pointsDelta = 0;
        let beepNeeded = false;

        /* ─── 1. Auto-finish station construction + extension construction ─── */
        let anyStationMutated = false;
        stations = stations.map((s) => {
          let next = s;
          // Tier construction
          if (next.buildCompleteAt && nextGameTime >= next.buildCompleteAt) {
            anyStationMutated = true;
            next = { ...next, buildStartedAt: undefined, buildCompleteAt: undefined };
          }
          // Phase 8.2 — Extension construction
          if (next.extensions && next.extensions.length > 0) {
            let dirty = false;
            const exts = next.extensions.map((e) => {
              if (e.buildCompleteAt && nextGameTime >= e.buildCompleteAt) {
                dirty = true;
                return { ...e, buildStartedAt: undefined, buildCompleteAt: undefined };
              }
              return e;
            });
            if (dirty) {
              anyStationMutated = true;
              next = { ...next, extensions: exts };
            }
          }
          return next;
        });

        /* ─── 2. Hospital slot release (drop expired patients) ─── */
        stations = stations.map((s) => {
          if (s.kind !== 'hospital' || !s.currentPatients?.length) return s;
          const before = s.currentPatients;
          const after = pruneExpiredPatients(before, nextGameTime);
          if (after.length !== before.length) {
            anyStationMutated = true;
            return { ...s, currentPatients: after };
          }
          return s;
        });
        if (anyStationMutated) {
          for (const s of stations) stationsByIdMutable.set(s.id, s);
        }

        /* ─── 2.5 Repair completion + low-condition warning (Phase 6.1) ─── */
        /* ─── + Phase 12.3 Modul-Tausch-Completion ─── */
        let anyVehicleMutated = false;
        const repairCompletionToasts: string[] = [];
        const moduleSwapCompletionToasts: string[] = [];
        const repairWarningToasts: { id: string; label: string; condition: number }[] = [];
        vehicles = vehicles.map((v) => {
          let next = v;
          // Repair finished?
          if (
            next.repairStartedAt !== undefined &&
            next.repairCompleteAt !== undefined &&
            nextGameTime >= next.repairCompleteAt
          ) {
            const spec = VEHICLE_SPECS[next.type];
            anyVehicleMutated = true;
            next = {
              ...next,
              status: 2 as VehicleStatus,
              condition: 1,
              waterTankCurrent: spec.waterTankMax,
              breathingAirCurrent: spec.breathingAirMax,
              repairStartedAt: undefined,
              repairCompleteAt: undefined,
              repairWarningShown: false, // reset so next degradation re-warns
            };
            repairCompletionToasts.push(spec.label);
          }
          // Phase 12.3 — Module swap finished?
          if (
            next.workshopUntilAt !== undefined &&
            next.pendingModuleSwap &&
            nextGameTime >= next.workshopUntilAt
          ) {
            const swap = next.pendingModuleSwap;
            // Apply: remove + add modules.
            const removed = new Set(swap.removeInstanceIds);
            const remainingInstalls = (next.installedModules ?? []).filter(
              (i) => !removed.has(i.id),
            );
            const addedInstalls = swap.addModuleIds.map((mid, idx) => ({
              id: `vmod_${nextGameTime}_${idx}_${Math.random().toString(36).slice(2, 5)}`,
              moduleId: mid,
              installedAt: nextGameTime,
            }));
            anyVehicleMutated = true;
            next = {
              ...next,
              status: 2 as VehicleStatus,
              workshopUntilAt: undefined,
              pendingModuleSwap: undefined,
              installedModules: [...remainingInstalls, ...addedInstalls],
            };
            moduleSwapCompletionToasts.push(
              next.displayName ?? VEHICLE_SPECS[next.type].label,
            );
          }
          // First-time low-condition warning
          if (
            next.condition <= REPAIR_DISABLE_THRESHOLD &&
            !next.repairWarningShown &&
            next.repairStartedAt === undefined
          ) {
            anyVehicleMutated = true;
            next = { ...next, repairWarningShown: true };
            repairWarningToasts.push({
              id: next.id,
              label: VEHICLE_SPECS[next.type].label,
              condition: next.condition,
            });
          }
          return next;
        });

        /* ─── 3. Mission expiration: fail open & dispatched/onscene past expiresAt ─── */
        const expiredOpenTitles: string[] = [];
        const expiredWorkedTitles: string[] = [];
        const expiredWorkedRewards: number[] = [];
        let anyMissionMutated = false;
        missions = missions.map((m) => {
          if (m.status === 'closed' || m.status === 'failed') return m;
          if (nextGameTime < m.expiresAt) return m;
          anyMissionMutated = true;
          if (m.status === 'open') {
            expiredOpenTitles.push(m.title);
          } else {
            // dispatched / onscene that timed out → half reward
            expiredWorkedTitles.push(m.title);
            expiredWorkedRewards.push(Math.round(m.payout * 0.5));
          }
          return {
            ...m,
            status: 'failed' as const,
            closedAt: nextGameTime,
            phase: 'closing' as const,
          };
        });

        // Vehicles assigned to expired missions get released (return to base).
        const expiredMissionIds = new Set(
          missions.filter((m) => m.status === 'failed' && m.closedAt === nextGameTime).map((m) => m.id),
        );

        /* ─── 3.5 Mission escalation (Phase 6.1) ─── */
        const escalationToasts: { from: string; to: string }[] = [];
        missions = missions.map((m) => {
          if (m.status === 'closed' || m.status === 'failed') return m;
          // Cooldown gate
          if (
            m.lastEscalatedAt !== undefined &&
            nextGameTime - m.lastEscalatedAt < ESCALATION_COOLDOWN_MS
          ) {
            return m;
          }
          const entry = getMissionCatalogEntry(m.catalogId);
          if (!entry?.escalation) return m;
          const since = m.lastEscalatedAt ?? m.spawnedAt;
          if (nextGameTime - since < entry.escalation.triggerAfterMs) return m;

          // Phase 8.4.7 — Eskalation greift nur, wenn Lage erlebt wurde, also
          // mindestens ein Fahrzeug Status 4 erreicht hat. Vorher ist es nur
          // ein Anrufer mit unklarer Lage; Kapazitäten würden falsch alarmieren.
          const anyArrived = vehicles.some(
            (v) => v.assignedMissionId === m.id && v.status === 4,
          );
          if (!anyArrived) return m;

          // Check trigger condition
          let shouldEscalate = false;
          switch (entry.escalation.trigger) {
            case 'open':
              // Phase 8.4.7 — pre-arrival path is gated above, so re-evaluate
              // as a shouldEscalate-once-arrived signal.
              shouldEscalate = true;
              break;
            case 'noScene': {
              const anyOnScene = vehicles.some(
                (v) => v.assignedMissionId === m.id && v.status === 4,
              );
              shouldEscalate = !anyOnScene;
              // Note: anyArrived gate above prevents pure 'open' escalation.
              if (m.status === 'open' || m.status === 'dispatched') {
                shouldEscalate = !anyOnScene;
              }
              break;
            }
            case 'lowProgress':
              shouldEscalate = m.status === 'onscene' && m.progress < ESCALATION_PROGRESS_FLOOR;
              break;
          }
          if (!shouldEscalate) return m;

          const target = getMissionCatalogEntry(entry.escalation.toCode);
          if (!target) return m;

          const remainingBefore = Math.max(0, m.expiresAt - nextGameTime);
          const newExpiresAt =
            nextGameTime + remainingBefore + remainingBefore * ESCALATION_TIMEOUT_BONUS_FACTOR;

          anyMissionMutated = true;
          escalationToasts.push({ from: entry.code, to: target.code });
          const newHistory: EscalationEntry[] = [
            ...(m.escalationHistory ?? []),
            {
              id: `esc_${nextGameTime}_${m.escalationCount}`,
              fromCode: entry.code,
              toCode: target.code,
              timestamp: nextGameTime,
            },
          ];
          return {
            ...m,
            catalogId: target.id,
            code: target.code,
            title: target.title,
            category: target.category,
            difficulty: target.difficulty,
            requiredVehicles: target.requiredVehicles.map((r) => ({ ...r })),
            patientCount: target.patientCount,
            durationMs: target.durationMinutes * 60_000,
            waterRequiredLiters: target.waterRequiredLiters,
            breathingAirRequired: target.breathingAirRequired,
            payout: MISSION_DIFFICULTY_REWARDS[target.difficulty],
            expiresAt: newExpiresAt,
            escalationCount: m.escalationCount + 1,
            lastEscalatedAt: nextGameTime,
            escalationHistory: newHistory,
          };
        });

        /* ─── 4. Vehicle state machine ─── */
        const stationsByIdNow = anyStationMutated ? stationsByIdMutable : stationsById;

        // (a) Alarm phase complete → Status 3, schedule route fetch via animation hook.
        // Phase 8.1.4 — track newly-departing vehicles for siren burst.
        // Phase 10.4.4 — Covert vehicles (MEK) deploy without siren burst.
        let anyDeparted = false;
        vehicles = vehicles.map((v) => {
          if (
            v.alarmStartedAt !== undefined &&
            v.alarmCompleteAt !== undefined &&
            v.status === 2 &&
            nextGameTime >= v.alarmCompleteAt
          ) {
            anyVehicleMutated = true;
            const spec = VEHICLE_SPECS[v.type];
            if (!spec.covertOperation) anyDeparted = true;
            return {
              ...v,
              status: 3 as VehicleStatus,
              alarmStartedAt: undefined,
              alarmCompleteAt: undefined,
            };
          }
          return v;
        });

        // (b) Released vehicles whose mission expired during transit → return home.
        if (expiredMissionIds.size > 0) {
          vehicles = vehicles.map((v) => {
            if (!v.assignedMissionId || !expiredMissionIds.has(v.assignedMissionId)) return v;
            anyVehicleMutated = true;
            const homeStation = stationsByIdNow.get(v.stationId);
            const homePos = homeStation?.position ?? v.position;
            // Mission was cancelled mid-transit. We don't track the live
            // animated position in store, so we snap back to home and let
            // the routing hook resume nothing (vehicle is already at base).
            return {
              ...clearTransitFields(v),
              status: 2 as VehicleStatus,
              assignedMissionId: undefined,
              destination: undefined,
              destinationHospitalId: undefined,
              alarmStartedAt: undefined,
              alarmCompleteAt: undefined,
              position: homePos,
            };
          });
        }

        // (c) Animated arrivals: vehicles whose leg has elapsed.
        const arrivedTransits: { vehicleId: string; toStatus: VehicleStatus }[] = [];
        vehicles = vehicles.map((v) => {
          if (
            (v.status === 3 || v.status === 7 || v.status === 1) &&
            v.legStartedAt !== undefined &&
            v.legDurationMs !== undefined &&
            nextGameTime - v.legStartedAt >= v.legDurationMs
          ) {
            arrivedTransits.push({ vehicleId: v.id, toStatus: v.status });
          }
          return v;
        });

        // Apply arrivals
        for (const arr of arrivedTransits) {
          const v = vehicles.find((x) => x.id === arr.vehicleId);
          if (!v) continue;
          anyVehicleMutated = true;

          if (arr.toStatus === 3) {
            // → Status 4 at scene
            const mission = missions.find((m) => m.id === v.assignedMissionId);
            if (!mission) continue;
            vehicles = vehicles.map((x) =>
              x.id === v.id
                ? {
                    ...clearTransitFields(x),
                    status: 4 as VehicleStatus,
                    position: mission.position,
                  }
                : x,
            );
            // Mission flips to onscene if any vehicle has arrived; arrivedAt on first arrival.
            // Phase 8.4 — also advance the lifecycle phase. If an ELW is already on
            // scene (or this arrival is the ELW), Erkundung wird übersprungen
            // (Sofort-Erkundung durch den Einsatzleiter).
            missions = missions.map((m) => {
              if (m.id !== mission.id) return m;
              if (m.status === 'closed' || m.status === 'failed') return m;
              anyMissionMutated = true;
              const isFirstArrival = m.arrivedAt === undefined;
              const elwOnScene =
                v.type === 'ELW' ||
                vehicles.some(
                  (x) =>
                    x.assignedMissionId === m.id &&
                    x.id !== v.id &&
                    x.status === 4 &&
                    x.type === 'ELW',
                );
              const nextPhase: Mission['phase'] = isFirstArrival
                ? elwOnScene
                  ? 'engagement'
                  : 'reconnaissance'
                : (m.phase ?? 'reconnaissance');
              // Phase 11.2.6 (N9) — Funk-Eintrag bei jedem Eintreffen.
              const arrivalLabel = VEHICLE_SPECS[v.type].label;
              const arrivalRadio: RadioMessage = {
                id: `radio_arr_${nextGameTime}_${v.id.slice(-4)}`,
                kind: 'situation-report',
                vehicleId: v.id,
                vehicleLabel: arrivalLabel,
                text: isFirstArrival
                  ? 'Eingetroffen, Erkundung läuft.'
                  : 'Eingetroffen, übernehme Auftrag.',
                timestamp: nextGameTime,
              };
              return {
                ...m,
                status: 'onscene' as const,
                arrivedAt: m.arrivedAt ?? nextGameTime,
                phase: nextPhase,
                reconnaissanceStartedAt:
                  m.reconnaissanceStartedAt ??
                  (isFirstArrival && !elwOnScene ? nextGameTime : undefined),
                reconnaissanceDone:
                  m.reconnaissanceDone || (isFirstArrival && elwOnScene),
                radioMessages: [...(m.radioMessages ?? []), arrivalRadio],
              };
            });
          } else if (arr.toStatus === 7) {
            // → Status 8 at hospital, occupy slot
            const hospital = v.destinationHospitalId
              ? stationsByIdNow.get(v.destinationHospitalId)
              : undefined;
            if (!hospital) continue;
            const patient: HospitalPatient = {
              id: `pat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              admittedAt: nextGameTime,
              freeAt: nextGameTime + HOSPITAL_TREATMENT_MS,
            };
            stations = stations.map((s) =>
              s.id === hospital.id
                ? { ...s, currentPatients: [...(s.currentPatients ?? []), patient] }
                : s,
            );
            anyStationMutated = true;
            stationsByIdNow.set(
              hospital.id,
              stations.find((s) => s.id === hospital.id) as Station,
            );

            vehicles = vehicles.map((x) =>
              x.id === v.id
                ? {
                    ...x,
                    status: 8 as VehicleStatus,
                    position: hospital.position,
                    legStartedAt: nextGameTime,
                    legDurationMs: HANDOVER_DURATION_MS,
                    routeCoords: undefined,
                    routeProgress: undefined,
                  }
                : x,
            );

            // Mission patientsDelivered increments
            missions = missions.map((m) =>
              m.id === v.assignedMissionId
                ? { ...m, patientsDelivered: m.patientsDelivered + 1 }
                : m,
            );
            anyMissionMutated = true;
          } else if (arr.toStatus === 1) {
            // → Status 2 at home, full refill
            const station = stationsByIdNow.get(v.stationId);
            const spec = VEHICLE_SPECS[v.type];
            const homePos = station?.position ?? v.position;

            // Phase 10.4.2 — Hand over any arrested persons to the police
            // station's holding cells. If we're returning to the courthouse
            // (after a transfer), the persons are released to the court (toast).
            const arrested = v.arrestedPersons ?? [];
            if (arrested.length > 0 && station) {
              if (station.kind === 'courthouse') {
                // shouldn't happen under normal flow; just clear.
              } else if (
                station.kind === 'police-station' ||
                station.kind === 'riot-police-station' ||
                station.kind === 'sek-base' ||
                station.kind === 'federal-police-station'
              ) {
                const newHeld = arrested.map((p) => ({
                  id: p.id,
                  name: p.name,
                  arrestedAt: p.arrestedAt,
                  forcedTransferAt: p.arrestedAt + 48 * 60 * 60_000,
                }));
                stations = stations.map((s) =>
                  s.id === station.id
                    ? {
                        ...s,
                        heldPersons: [...(s.heldPersons ?? []), ...newHeld],
                      }
                    : s,
                );
                anyStationMutated = true;
                for (const p of arrested) {
                  toastsToPush.push({
                    kind: 'info',
                    text: `🔒 ${p.name} in Wachzelle (${station.name})`,
                  });
                }
              }
            }

            vehicles = vehicles.map((x) =>
              x.id === v.id
                ? {
                    ...clearTransitFields(x),
                    status: 2 as VehicleStatus,
                    position: homePos,
                    waterTankCurrent: spec.waterTankMax,
                    breathingAirCurrent: spec.breathingAirMax,
                    assignedMissionId: undefined,
                    destination: undefined,
                    destinationHospitalId: undefined,
                    arrestedPersons: undefined,
                  }
                : x,
            );
          }
        }

        // (d) Status 8 handover finished → Status 1 (return home)
        vehicles = vehicles.map((v) => {
          if (
            v.status === 8 &&
            v.legStartedAt !== undefined &&
            v.legDurationMs !== undefined &&
            nextGameTime - v.legStartedAt >= v.legDurationMs
          ) {
            anyVehicleMutated = true;
            return {
              ...clearTransitFields(v),
              status: 1 as VehicleStatus,
              destination: 'station' as const,
              destinationHospitalId: undefined,
              // routeCoords cleared; routing hook will fetch hospital → station path
            };
          }
          return v;
        });

        /* ─── 5. Status 4 work + resource drain (per mission) ─── */
        const onsceneMissions = missions.filter((m) => m.status === 'onscene');
        const drainStallToasts: string[] = [];
        const airWarningVehicleIds = new Set<string>();
        const hydrantConnections = new Map<string, string>();

        // Phase 7.4.2 — proactive low-water warnings
        const lowWaterWarnings: { vehicleId: string; missionId: string; label: string }[] = [];
        // Phase 7.2.2 — radio messages collected this tick
        const radioMessages: { missionId: string; msg: RadioMessage; sound: boolean }[] = [];
        // Phase 9.4 — neue Sprechwünsche, die im Floating-Card landen
        const newRadioRequests: RadioRequest[] = [];
        // Phase 7.2.2 — request flag updates per mission
        const radioFlagUpdates = new Map<string, Mission['radioRequestFlags']>();

        if (onsceneMissions.length > 0 && dtGame > 0) {
          const vehiclesById = new Map(vehicles.map((v) => [v.id, v]));
          const updatedVehiclesByMission = new Map<string, Vehicle[]>();
          const stalledMissionIds = new Set<string>();
          const newlyStalledMissionIds = new Set<string>();
          // Phase 7.2.5 — module progress multiplier per mission (geometric mean over participants)
          const moduleProgressMultByMission = new Map<string, number>();

          for (const m of onsceneMissions) {
            const participants = m.assignedVehicleIds
              .map((id) => vehiclesById.get(id))
              .filter((v): v is Vehicle => v !== undefined && v.status === 4);

            if (participants.length === 0) continue;

            const drain = applyResourceDrain(
              participants,
              m,
              dtGame,
              state.hydrants,
              state.waterFeatures ?? [],
            );
            for (const w of drain.airWarnings) airWarningVehicleIds.add(w);
            for (const [vid, hid] of drain.hydrantConnections) {
              hydrantConnections.set(vid, hid);
            }

            updatedVehiclesByMission.set(m.id, drain.vehicles);
            if (drain.stalled) stalledMissionIds.add(m.id);

            // Module progress multiplier
            let mult = 1;
            for (const p of participants) {
              const inst = p.installedModules ?? [];
              const eff = combinedEffects(inst.map((i) => i.moduleId));
              if (eff.progressMultiplier) mult *= eff.progressMultiplier;
            }
            if (mult !== 1) moduleProgressMultByMission.set(m.id, mult);

            // Phase 7.4.2 — proactive low-water warning
            // For each tank-pumper at scene, project remaining time at current drain.
            for (const p of drain.vehicles) {
              if (!p.connectedHydrantId && p.waterTankCurrent > 0 && p.waterTankMax > 0) {
                const drainPerSec = 200 / 60; // L/sec
                const remainingSec = p.waterTankCurrent / drainPerSec;
                if (remainingSec < 30 && p.lowResourceWarnedFor !== m.id) {
                  lowWaterWarnings.push({
                    vehicleId: p.id,
                    missionId: m.id,
                    label: VEHICLE_SPECS[p.type].label,
                  });
                }
              }
            }

            // Phase 7.2.2 — radio messages from on-scene vehicles
            const flags = m.radioRequestFlags ?? {};
            const newFlags: Mission['radioRequestFlags'] = { ...flags };

            // (a) NEF nachfordern: if mission has patients > 1 OR escalated, and no NEF on scene
            if (
              !flags.nefRequested &&
              m.patientCount > 1 &&
              !participants.some((p) => p.type === 'NEF')
            ) {
              if (Math.random() < 0.005 * (dtGame / 1000)) {
                // Probabilistic per second
                const rtw = participants.find((p) => p.type === 'RTW');
                if (rtw) {
                  newFlags.nefRequested = true;
                  const reqId = `req_${nextGameTime}_${Math.random().toString(36).slice(2, 6)}`;
                  radioMessages.push({
                    missionId: m.id,
                    msg: {
                      id: reqId,
                      kind: 'request-nef',
                      vehicleId: rtw.id,
                      vehicleLabel: VEHICLE_SPECS[rtw.type].label,
                      text: 'Notarzt nachgefordert — mehr Verletzte als erwartet.',
                      timestamp: nextGameTime,
                    },
                    sound: true,
                  });
                  newRadioRequests.push({
                    id: reqId,
                    kind: 'request-nef',
                    missionId: m.id,
                    vehicleId: rtw.id,
                    vehicleLabel: VEHICLE_SPECS[rtw.type].label,
                    text: 'Notarzt nachgefordert — mehr Verletzte als erwartet.',
                    createdAt: nextGameTime,
                  });
                }
              }
            }

            // (b) DLK nachfordern: if F-mission with hard+ difficulty and no DLK
            if (
              !flags.dlkRequested &&
              m.category === 'fire' &&
              (m.difficulty === 'hard' || m.difficulty === 'expert' || m.difficulty === 'extreme') &&
              !participants.some((p) => p.type === 'DLK')
            ) {
              if (Math.random() < 0.005 * (dtGame / 1000)) {
                const lf = participants.find((p) => p.type === 'LF' || p.type === 'HLF');
                if (lf) {
                  newFlags.dlkRequested = true;
                  const reqId = `req_${nextGameTime}_${Math.random().toString(36).slice(2, 6)}`;
                  radioMessages.push({
                    missionId: m.id,
                    msg: {
                      id: reqId,
                      kind: 'request-dlk',
                      vehicleId: lf.id,
                      vehicleLabel: VEHICLE_SPECS[lf.type].label,
                      text: 'Drehleiter nachgefordert — Höhenrettung nötig.',
                      timestamp: nextGameTime,
                    },
                    sound: true,
                  });
                  newRadioRequests.push({
                    id: reqId,
                    kind: 'request-dlk',
                    missionId: m.id,
                    vehicleId: lf.id,
                    vehicleLabel: VEHICLE_SPECS[lf.type].label,
                    text: 'Drehleiter nachgefordert — Höhenrettung nötig.',
                    createdAt: nextGameTime,
                  });
                }
              }
            }

            // (c) Wasser nachfordern: if a pumper is < 25 % water and no TLF/SW present
            if (
              !flags.waterRequested &&
              !participants.some((p) => p.type === 'TLF' || p.type === 'SW')
            ) {
              const lowPumper = participants.find(
                (p) =>
                  p.waterTankMax > 0 &&
                  p.waterTankCurrent / p.waterTankMax < 0.25 &&
                  !p.connectedHydrantId,
              );
              if (lowPumper && Math.random() < 0.01 * (dtGame / 1000)) {
                newFlags.waterRequested = true;
                const reqId = `req_${nextGameTime}_${Math.random().toString(36).slice(2, 6)}`;
                radioMessages.push({
                  missionId: m.id,
                  msg: {
                    id: reqId,
                    kind: 'request-water',
                    vehicleId: lowPumper.id,
                    vehicleLabel: VEHICLE_SPECS[lowPumper.type].label,
                    text: 'Wassernachschub angefordert — Tank fast leer, kein Hydrant in Reichweite.',
                    timestamp: nextGameTime,
                  },
                  sound: true,
                });
                newRadioRequests.push({
                  id: reqId,
                  kind: 'request-water',
                  missionId: m.id,
                  vehicleId: lowPumper.id,
                  vehicleLabel: VEHICLE_SPECS[lowPumper.type].label,
                  text: 'Wassernachschub angefordert — Tank fast leer, kein Hydrant in Reichweite.',
                  createdAt: nextGameTime,
                });
              }
            }

            // (d) Lagemeldung: random every 60–120 game-seconds
            const sinceLast = newFlags.lastSituationReportAt
              ? nextGameTime - newFlags.lastSituationReportAt
              : 60_000;
            if (sinceLast > 60_000 && Math.random() < 0.008 * (dtGame / 1000)) {
              const speaker = participants[0];
              if (speaker) {
                newFlags.lastSituationReportAt = nextGameTime;
                const reports = [
                  'Lage unter Kontrolle, Arbeiten fortgeschritten.',
                  'Ausbreitung gestoppt, Nachlöscharbeiten laufen.',
                  'Patient stabilisiert, Transport vorbereitet.',
                  'Erkundung abgeschlossen, Rückfahrt beginnt in Kürze.',
                ];
                radioMessages.push({
                  missionId: m.id,
                  msg: {
                    id: `radio_${nextGameTime}_${Math.random().toString(36).slice(2, 6)}`,
                    kind: 'situation-report',
                    vehicleId: speaker.id,
                    vehicleLabel: VEHICLE_SPECS[speaker.type].label,
                    text: reports[Math.floor(Math.random() * reports.length)],
                    timestamp: nextGameTime,
                  },
                  sound: false,
                });
              }
            }

            if (Object.keys(newFlags).length !== Object.keys(flags).length || newFlags !== flags) {
              radioFlagUpdates.set(m.id, newFlags);
            }
          }

          // Apply drained tanks + hydrant links back into the canonical vehicles list
          if (updatedVehiclesByMission.size > 0 || hydrantConnections.size > 0) {
            const updates = new Map<string, Vehicle>();
            for (const arr of updatedVehiclesByMission.values()) {
              for (const v of arr) updates.set(v.id, v);
            }
            vehicles = vehicles.map((v) => {
              const drained = updates.get(v.id);
              const base = drained ?? v;
              const connected = hydrantConnections.get(v.id);
              if (connected !== undefined && base.connectedHydrantId !== connected) {
                anyVehicleMutated = true;
                return { ...base, connectedHydrantId: connected };
              }
              if (connected === undefined && base.connectedHydrantId !== undefined && base.status === 4) {
                anyVehicleMutated = true;
                return { ...base, connectedHydrantId: undefined };
              }
              if (drained) anyVehicleMutated = true;
              return base;
            });
          }

          // Mark vehicles that triggered a low-water warning so we don't repeat per-mission.
          if (lowWaterWarnings.length > 0) {
            const warnSet = new Map<string, string>(); // vid → missionId
            for (const w of lowWaterWarnings) warnSet.set(w.vehicleId, w.missionId);
            vehicles = vehicles.map((v) =>
              warnSet.has(v.id)
                ? { ...v, lowResourceWarnedFor: warnSet.get(v.id) }
                : v,
            );
            anyVehicleMutated = true;
          }

          // Advance mission progress for non-stalled missions, mark stalls,
          // append radio messages + flag updates.
          // Phase 8.4 — During reconnaissance (30 s after first arrival), progress
          // pauses while the Einsatzleiter erkundet. ELW arrivals skip recon.
          // Phase 10.4.2 — collect arrest measures that just completed (mission, count).
          const arrestsJustCompletedByMission = new Map<string, number>();
          missions = missions.map((m) => {
            if (m.status !== 'onscene') return m;
            const stalled = stalledMissionIds.has(m.id);
            const newRadio = radioMessages.filter((r) => r.missionId === m.id).map((r) => r.msg);
            const flagPatch = radioFlagUpdates.get(m.id);
            const baseFlags = flagPatch ?? m.radioRequestFlags;

            // Phase advancement — recon → engagement
            let phasePatch: Partial<Mission> | null = null;
            if (
              m.phase === 'reconnaissance' &&
              !m.reconnaissanceDone &&
              m.reconnaissanceStartedAt !== undefined &&
              nextGameTime - m.reconnaissanceStartedAt >= RECONNAISSANCE_DURATION_MS
            ) {
              phasePatch = { phase: 'engagement', reconnaissanceDone: true };
              anyMissionMutated = true;
            }
            // ELW shows up later? Skip remaining recon time.
            if (
              m.phase === 'reconnaissance' &&
              !m.reconnaissanceDone &&
              vehicles.some(
                (v) =>
                  v.assignedMissionId === m.id && v.status === 4 && v.type === 'ELW',
              )
            ) {
              phasePatch = { phase: 'engagement', reconnaissanceDone: true };
              anyMissionMutated = true;
            }

            if (stalled) {
              if (!m.stallToastShown) {
                drainStallToasts.push(m.title);
                newlyStalledMissionIds.add(m.id);
                anyMissionMutated = true;
              }
              if (newRadio.length > 0 || flagPatch || phasePatch) {
                anyMissionMutated = true;
                return {
                  ...m,
                  ...(phasePatch ?? {}),
                  stallToastShown: m.stallToastShown ?? !m.stallToastShown,
                  radioMessages: [...(m.radioMessages ?? []), ...newRadio],
                  radioRequestFlags: baseFlags,
                };
              }
              if (!m.stallToastShown) return { ...m, stallToastShown: true };
              return m;
            }

            // Module progress multiplier
            const mult = moduleProgressMultByMission.get(m.id) ?? 1;
            // Progress only advances once we are in engagement (not during recon).
            const inEngagement =
              (phasePatch?.phase ?? m.phase) === 'engagement' ||
              m.reconnaissanceDone === true;

            // Phase 9.1 — Maßnahmen-Fulfillment + per-Maßnahme-Progress.
            // Phase 10.3.2 — Cross-org gating: blockedBy / blocksOtherMeasures /
            // requiredVehicleTypes / minVehicleCount.
            let nextMeasures = m.measures;
            let measuresMutated = false;
            if (inEngagement && m.measures && m.measures.length > 0) {
              const sceneInstalledModuleIds = new Set<string>();
              const sceneVehicleTypes: import('./types').VehicleType[] = [];
              for (const v of vehicles) {
                if (v.assignedMissionId !== m.id || v.status !== 4) continue;
                sceneVehicleTypes.push(v.type);
                for (const inst of v.installedModules ?? []) {
                  sceneInstalledModuleIds.add(inst.moduleId);
                }
              }

              // First pass: which measures are currently "active" (used to gate
              // their `blocksOtherMeasures` siblings).
              const completedTemplateIds = new Set(
                m.measures.filter((x) => x.status === 'completed').map((x) => x.templateId),
              );
              // Templates whose owners ALSO are blocking siblings.
              const activelyBlocking = new Set<string>();
              for (const meas of m.measures) {
                if (meas.status === 'active' && meas.blocksOtherMeasures) {
                  for (const blocked of meas.blocksOtherMeasures) {
                    activelyBlocking.add(blocked);
                  }
                }
              }

              nextMeasures = m.measures.map((meas) => {
                if (meas.status === 'completed') return meas;
                // Auto-complete Patiententransport once patients are delivered.
                if (
                  meas.templateId === 'patiententransport' &&
                  m.patientCount > 0 &&
                  m.patientsDelivered >= m.patientCount
                ) {
                  measuresMutated = true;
                  return { ...meas, status: 'completed', progress: 1 };
                }

                // Phase 10.3.2 — Gating checks.
                const blockedByPending = (meas.blockedBy ?? []).some(
                  (id) => !completedTemplateIds.has(id),
                );
                const blockedByActiveSibling = activelyBlocking.has(meas.templateId);
                const isGated = blockedByPending || blockedByActiveSibling;

                // Re-evaluate module fulfillment (independent of gating).
                const newReqs = meas.requiredModules.map((r) => ({
                  ...r,
                  fulfilled: Array.from(sceneInstalledModuleIds).some((id) =>
                    moduleSatisfies(id, r),
                  ),
                }));
                const modulesOk = newReqs.every((r) => r.fulfilled);

                // requiredVehicleTypes + minVehicleCount checks.
                let vehicleTypesOk = true;
                if (meas.requiredVehicleTypes && meas.requiredVehicleTypes.length > 0) {
                  const need = meas.minVehicleCount ?? 1;
                  const matching = sceneVehicleTypes.filter((t) =>
                    meas.requiredVehicleTypes!.includes(t),
                  ).length;
                  vehicleTypesOk = matching >= need;
                } else if (meas.minVehicleCount && meas.minVehicleCount > 0) {
                  vehicleTypesOk = sceneVehicleTypes.length >= meas.minVehicleCount;
                }

                const allFulfilled = !isGated && modulesOk && vehicleTypesOk;

                let nextStatus: Measure['status'] = meas.status;
                let nextStartedAt = meas.startedAt;
                let nextProgress = meas.progress;
                if (allFulfilled) {
                  if (meas.status === 'pending') {
                    nextStatus = 'active';
                    nextStartedAt = nextGameTime;
                  } else if (meas.status === 'fulfilled') {
                    nextStatus = 'active';
                  }
                  const inc = (dtGame / meas.durationMs) * mult;
                  nextProgress = Math.min(1, meas.progress + inc);
                  if (nextProgress >= 1) {
                    nextStatus = 'completed';
                    // Phase 10.4.2 — arrest tracking
                    if (
                      meas.organization === 'police' &&
                      (meas.category === 'arrest' ||
                        meas.templateId === 'verhaftung' ||
                        meas.templateId === 'sek-stuermen' ||
                        meas.templateId === 'bank-sichern')
                    ) {
                      arrestsJustCompletedByMission.set(
                        m.id,
                        (arrestsJustCompletedByMission.get(m.id) ?? 0) + 1,
                      );
                    }
                  }
                } else if (meas.status === 'active') {
                  // Lost a precondition mid-flight — pause progress.
                  nextStatus = isGated ? 'pending' : 'fulfilled';
                }
                const reqsChanged = newReqs.some(
                  (r, i) => r.fulfilled !== meas.requiredModules[i]?.fulfilled,
                );
                if (
                  reqsChanged ||
                  nextStatus !== meas.status ||
                  nextProgress !== meas.progress ||
                  nextStartedAt !== meas.startedAt
                ) {
                  measuresMutated = true;
                  return {
                    ...meas,
                    requiredModules: newReqs,
                    status: nextStatus,
                    startedAt: nextStartedAt,
                    progress: nextProgress,
                  };
                }
                return meas;
              });
            }

            // Phase 9.1 — Mission progress is the average of measure progresses.
            // For legacy missions without measures, fall back to the old timer-based
            // progress so saves from < v10 behave as before.
            let newProgress: number;
            const hasMeasures = nextMeasures && nextMeasures.length > 0;
            if (hasMeasures) {
              const sum = nextMeasures!.reduce((a, x) => a + x.progress, 0);
              newProgress = sum / nextMeasures!.length;
            } else {
              const inc = inEngagement ? (dtGame / m.durationMs) * mult : 0;
              newProgress = Math.min(1, m.progress + inc);
            }

            // Phase 8.4.6 — fire intensity decays as progress increases.
            let nextIntensity = m.fireIntensity;
            if (m.category === 'fire' && nextIntensity !== undefined && inEngagement) {
              const delta = Math.max(0, newProgress - m.progress);
              nextIntensity = Math.max(0.05, m.fireIntensity! * (1 - delta * 1.2));
            }

            const dirty =
              newProgress !== m.progress ||
              newRadio.length > 0 ||
              flagPatch !== undefined ||
              phasePatch !== null ||
              nextIntensity !== m.fireIntensity ||
              measuresMutated;
            if (!dirty) return m;
            anyMissionMutated = true;
            return {
              ...m,
              ...(phasePatch ?? {}),
              progress: newProgress,
              fireIntensity: nextIntensity,
              measures: nextMeasures,
              radioMessages:
                newRadio.length > 0 ? [...(m.radioMessages ?? []), ...newRadio] : m.radioMessages,
              radioRequestFlags: baseFlags,
            };
          });

          // Phase 10.4.2 — Process all arrest completions that happened this tick.
          // Phase 11.2.6 (N9) — Auch eine RadioMessage in mission.radioMessages
          // pushen, damit der Funk-Tab den Verlauf zeigt.
          for (const [missionId, count] of arrestsJustCompletedByMission) {
            const carriers = vehicles.filter(
              (v) =>
                v.assignedMissionId === missionId &&
                v.status === 4 &&
                (v.type === 'FuStW' || v.type === 'GruKW'),
            );
            for (let i = 0; i < count; i++) {
              if (i >= carriers.length) break;
              const carrier = carriers[i];
              const newPerson = {
                id: `arr_${nextGameTime}_${i}_${Math.random()
                  .toString(36)
                  .slice(2, 5)}`,
                name: makeRandomName(),
                arrestedAt: nextGameTime,
                needsTransport: true,
              };
              vehicles = vehicles.map((v) =>
                v.id === carrier.id
                  ? {
                      ...v,
                      arrestedPersons: [...(v.arrestedPersons ?? []), newPerson],
                    }
                  : v,
              );
              anyVehicleMutated = true;
              toastsToPush.push({
                kind: 'success',
                text: `🚓 Festnahme: ${newPerson.name} ist im Streifenwagen.`,
              });
              // Append the same event to the mission's radio log.
              const carrierLabel = VEHICLE_SPECS[carrier.type].label;
              const radioMsg: RadioMessage = {
                id: `radio_arr_${nextGameTime}_${i}_${Math.random().toString(36).slice(2, 5)}`,
                kind: 'situation-report',
                vehicleId: carrier.id,
                vehicleLabel: carrierLabel,
                text: `Person festgenommen: ${newPerson.name}.`,
                timestamp: nextGameTime,
              };
              missions = missions.map((mm) =>
                mm.id === missionId
                  ? { ...mm, radioMessages: [...(mm.radioMessages ?? []), radioMsg] }
                  : mm,
              );
              anyMissionMutated = true;
            }
          }
        }

        /* ─── 6. Mission scene-cleared transitions (progress hits 1) ─── */
        // For each onscene mission with progress >= 1: dispatch transports / send vehicles home / close.
        const closedSuccessIds = new Set<string>();
        const closedSuccessRewards: number[] = [];

        for (const m of missions) {
          if (m.status !== 'onscene') continue;
          if (m.progress < 1) continue;

          // Vehicles currently at scene for this mission
          const sceneVehicles = vehicles.filter(
            (v) => v.assignedMissionId === m.id && v.status === 4,
          );
          if (sceneVehicles.length === 0) continue;

          const undeliveredPatients = m.patientCount - m.patientsDelivered;
          // Pick transport vehicles to handle the patients (limited by undelivered count).
          const transportPicks: Vehicle[] = [];
          for (const v of sceneVehicles) {
            if (transportPicks.length >= undeliveredPatients) break;
            if (isTransportVehicle(v.type)) transportPicks.push(v);
          }

          // Transition each scene vehicle: transport (Status 7) or return (Status 1).
          for (const v of sceneVehicles) {
            anyVehicleMutated = true;
            if (transportPicks.includes(v)) {
              vehicles = vehicles.map((x) =>
                x.id === v.id
                  ? {
                      ...clearTransitFields(x),
                      status: 7 as VehicleStatus,
                      destination: 'hospital' as const,
                      destinationHospitalId: undefined, // hospital chosen by routing hook
                    }
                  : x,
              );
            } else {
              vehicles = vehicles.map((x) =>
                x.id === v.id
                  ? {
                      ...clearTransitFields(x),
                      status: 1 as VehicleStatus,
                      destination: 'station' as const,
                      assignedMissionId:
                        // Non-transport vehicles can drop the mission link;
                        // they no longer participate in delivery.
                        m.patientCount > 0 ? x.assignedMissionId : undefined,
                    }
                  : x,
              );
            }
          }

          // Mission closure logic: close if no patients remained OR all delivered.
          if (transportPicks.length === 0 && undeliveredPatients === 0) {
            anyMissionMutated = true;
            missions = missions.map((mm) =>
              mm.id === m.id
                ? { ...mm, status: 'closed' as const, closedAt: nextGameTime }
                : mm,
            );
            closedSuccessIds.add(m.id);
            if (state.mode !== 'sandbox') closedSuccessRewards.push(m.payout);
          }
          // If patients exist, mission stays 'onscene' until last RTW reaches Status 8.
          // Once patientsDelivered === patientCount, we close in (7) below.
        }

        // Phase 8.4 — set phase=closing for everything we just closed.
        if (closedSuccessIds.size > 0) {
          missions = missions.map((mm) =>
            closedSuccessIds.has(mm.id) ? { ...mm, phase: 'closing' as const } : mm,
          );
        }

        /* ─── 7. Close missions whose patients are all delivered ─── */
        for (const m of missions) {
          if (m.status !== 'onscene') continue;
          if (m.patientCount === 0) continue;
          if (m.patientsDelivered < m.patientCount) continue;
          // Are there any participants still in Status 4? If so, scene work isn't done yet.
          const stillScene = vehicles.some(
            (v) => v.assignedMissionId === m.id && v.status === 4,
          );
          if (stillScene) continue;

          anyMissionMutated = true;
          missions = missions.map((mm) =>
            mm.id === m.id
              ? {
                  ...mm,
                  status: 'closed' as const,
                  closedAt: nextGameTime,
                  phase: 'closing' as const,
                }
              : mm,
          );
          closedSuccessIds.add(m.id);
          if (state.mode !== 'sandbox') closedSuccessRewards.push(m.payout);
        }

        /* ─── 8. Generator: spawn one new mission if due (Phase 7.2.1) ─── */
        const readyStations = stations.filter((s) => isStationReady(s, nextGameTime));
        const activeAfterExpiry = missions.filter((m) => isActiveMissionStatus(m.status));
        const dynamicCap = dynamicMaxMissions(readyStations.length);

        let spawnedMission: Mission | null = null;
        if (state.homeCity) {
          if (nextMissionAt === undefined) {
            nextMissionAt =
              nextGameTime + computeSpawnIntervalMs(state, readyStations.length);
          }

          if (
            nextGameTime >= nextMissionAt &&
            readyStations.length > 0 &&
            activeAfterExpiry.length < dynamicCap
          ) {
            const entry = pickCatalogEntry(
              undefined,
              state.sandboxConfig.policeMissionSharePct ?? 30,
            );
            const radiusKm = getRadiusKm(state.level);
            const pos = spawnPosition(state.homeCity.center, radiusKm);
            const timeoutMin = MISSION_PENALTY_MODIFIERS[state.mode].timeoutMinutes;
            spawnedMission = {
              id: `msn_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              catalogId: entry.id,
              code: entry.code,
              title: entry.title,
              category: entry.category,
              difficulty: entry.difficulty,
              position: pos,
              requiredVehicles: entry.requiredVehicles.map((r: MissionRequirement) => ({ ...r })),
              assignedVehicleIds: [],
              payout: MISSION_DIFFICULTY_REWARDS[entry.difficulty],
              patientCount: entry.patientCount,
              patientsDelivered: 0,
              waterRequiredLiters: entry.waterRequiredLiters,
              breathingAirRequired: entry.breathingAirRequired,
              spawnedAt: nextGameTime,
              expiresAt: nextGameTime + timeoutMin * 60_000,
              status: 'open',
              durationMs: entry.durationMinutes * 60_000,
              progress: 0,
              escalationCount: 0,
              radioMessages: [],
              escalationHistory: [],
              // Phase 8.4 — initial narrative state
              phase: 'callout',
              callerStatement: entry.callerStatement ?? '',
              fireIntensity:
                entry.category === 'fire'
                  ? Math.min(
                      1,
                      0.4 +
                        (entry.difficulty === 'easy'
                          ? 0
                          : entry.difficulty === 'medium'
                            ? 0.15
                            : entry.difficulty === 'hard'
                              ? 0.3
                              : entry.difficulty === 'expert'
                                ? 0.45
                                : 0.6),
                    )
                  : undefined,
              // Phase 9.1 — Maßnahmen aus Catalog ableiten (sichtbar erst nach Erkundung).
              measures: makeMeasuresForCatalog(entry.id),
            };
            missions = [...missions, spawnedMission];
            anyMissionMutated = true;

            nextMissionAt =
              nextGameTime + computeSpawnIntervalMs(state, readyStations.length);
          } else if (
            nextGameTime >= nextMissionAt &&
            (readyStations.length === 0 || activeAfterExpiry.length >= dynamicCap)
          ) {
            nextMissionAt = nextGameTime + 5_000;
          }
        }

        /* ─── 9. Toast / point side effects assembly ─── */
        for (const t of expiredOpenTitles) {
          toastsToPush.push({ kind: 'error', text: `Einsatz verpasst: ${t}` });
        }
        for (let i = 0; i < expiredWorkedTitles.length; i++) {
          toastsToPush.push({
            kind: 'error',
            text: `Einsatz fehlgeschlagen: ${expiredWorkedTitles[i]} (Teilauszahlung)`,
          });
          creditsDelta += expiredWorkedRewards[i];
        }
        // Half-rewards: in sandbox no money but we still credit-add 0 (no-op).
        if (state.mode === 'sandbox') creditsDelta = 0;

        const failureCount = expiredOpenTitles.length + expiredWorkedTitles.length;
        if (failureCount > 0) {
          pointsDelta += pointsFor('missionFailure', state.mode) * failureCount;
        }

        for (const reward of closedSuccessRewards) creditsDelta += reward;
        if (closedSuccessIds.size > 0) {
          pointsDelta += pointsFor('missionSuccess', state.mode) * closedSuccessIds.size;
          // Apply -1 % condition wear to every vehicle that participated in a successful mission.
          const wornVehicleIds = new Set<string>();
          for (const id of closedSuccessIds) {
            const m = missions.find((x) => x.id === id);
            if (!m) continue;
            for (const vid of m.assignedVehicleIds) wornVehicleIds.add(vid);
          }
          if (wornVehicleIds.size > 0) {
            vehicles = vehicles.map((v) =>
              wornVehicleIds.has(v.id)
                ? { ...v, condition: Math.max(0, v.condition - 0.01) }
                : v,
            );
            anyVehicleMutated = true;
          }
          for (const id of closedSuccessIds) {
            const m = missions.find((x) => x.id === id);
            if (!m) continue;
            const rewardText =
              state.mode === 'sandbox' ? '' : ` (+${m.payout.toLocaleString('de-DE')} €)`;
            toastsToPush.push({
              kind: 'success',
              text: `Einsatz abgeschlossen: ${m.title}${rewardText}`,
            });
          }
        }

        for (const t of drainStallToasts) {
          toastsToPush.push({
            kind: 'warning',
            text: `Einsatz stockt: Wasser-/Atemluft-Versorgung erschöpft (${t})`,
          });
        }

        if (airWarningVehicleIds.size > 0) {
          // Per-vehicle warning toast
          for (const vid of airWarningVehicleIds) {
            const v = vehicles.find((x) => x.id === vid);
            if (!v) continue;
            const spec = VEHICLE_SPECS[v.type];
            toastsToPush.push({
              kind: 'warning',
              text: `${spec.label}: Atemluft unter 25 % – Reserve zur Wache schicken!`,
            });
          }
        }

        if (spawnedMission) {
          toastsToPush.push({
            kind: 'info',
            text: `Neuer Notruf: ${spawnedMission.code} ${spawnedMission.title}`,
          });
          beepNeeded = true;
        }

        // Phase 6.1 — repair / escalation toasts
        for (const label of repairCompletionToasts) {
          toastsToPush.push({
            kind: 'success',
            text: `${label}: Reparatur abgeschlossen, einsatzbereit.`,
          });
        }
        // Phase 12.3 — Modul-Tausch fertig
        for (const label of moduleSwapCompletionToasts) {
          toastsToPush.push({
            kind: 'success',
            text: `🔧 ${label}: Modul-Tausch abgeschlossen, wieder einsatzbereit.`,
          });
        }
        for (const w of repairWarningToasts) {
          toastsToPush.push({
            kind: 'warning',
            text: `🔧 ${w.label} benötigt Reparatur (Kondition ${Math.round(w.condition * 100)} %)`,
          });
        }
        for (const e of escalationToasts) {
          toastsToPush.push({
            kind: 'warning',
            text: `⚠️ Einsatz eskaliert: ${e.from} → ${e.to}`,
          });
        }

        // Phase 7.2.2 — radio toasts
        for (const rm of radioMessages) {
          toastsToPush.push({
            kind: 'info',
            text: `📻 ${rm.msg.vehicleLabel}: ${rm.msg.text}`,
          });
        }

        // Phase 7.4.2 — low-water toasts
        for (const w of lowWaterWarnings) {
          toastsToPush.push({
            kind: 'warning',
            text: `⚠️ ${w.label} in ca. 30 s ohne Wasser. Hydrant prüfen oder TLF nachalarmieren.`,
          });
        }

        /* ─── 9b. Stats accumulation (Phase 7.4.3) ─── */
        let statsDelta: Partial<GameStats> | null = null;
        if (
          closedSuccessRewards.length > 0 ||
          closedSuccessIds.size > 0 ||
          expiredOpenTitles.length > 0 ||
          expiredWorkedTitles.length > 0 ||
          dtGame > 0
        ) {
          statsDelta = {};
          // Real-time playtime increment
          if (deltaMs > 0) {
            statsDelta.playtimeSec = state.stats.playtimeSec + deltaMs / 1000;
          }
          if (closedSuccessRewards.length > 0) {
            const totalReward = closedSuccessRewards.reduce((a, b) => a + b, 0);
            statsDelta.totalIncome = state.stats.totalIncome + totalReward;
          }
          if (closedSuccessIds.size > 0) {
            statsDelta.missionsCompleted = state.stats.missionsCompleted + closedSuccessIds.size;
            const byCode = { ...state.stats.missionsByCode };
            for (const id of closedSuccessIds) {
              const m = missions.find((x) => x.id === id);
              if (!m) continue;
              byCode[m.code] = (byCode[m.code] ?? 0) + 1;
            }
            statsDelta.missionsByCode = byCode;
          }
          const failedCount = expiredOpenTitles.length + expiredWorkedTitles.length;
          if (failedCount > 0) {
            statsDelta.missionsFailed = state.stats.missionsFailed + failedCount;
          }
        }

        /* ─── 9c. Phase 8.3.2 — Tagesunterhalt am Spieltagesende abziehen ─── */
        let upkeepCharged = 0;
        let upkeepDays = 0;
        let nextUpkeepTickAt = state.lastUpkeepTickAt ?? state.gameTime;
        if (state.mode !== 'sandbox') {
          // Initialise on first tick to "now", so day-1 upkeep fires after a full day.
          if (state.lastUpkeepTickAt === undefined) {
            nextUpkeepTickAt = state.gameTime;
          }
          while (nextGameTime >= nextUpkeepTickAt + GAME_DAY_MS) {
            nextUpkeepTickAt += GAME_DAY_MS;
            upkeepCharged += state.dailyUpkeep;
            upkeepDays += 1;
          }
          if (upkeepCharged > 0) {
            creditsDelta -= upkeepCharged;
            toastsToPush.push({
              kind: 'info',
              text:
                upkeepDays === 1
                  ? `Unterhalt: -${upkeepCharged.toLocaleString('de-DE')} €`
                  : `Unterhalt (${upkeepDays} Tage): -${upkeepCharged.toLocaleString('de-DE')} €`,
            });
            statsDelta = statsDelta ?? {};
            statsDelta.spentOnUpkeep = state.stats.spentOnUpkeep + upkeepCharged;
          }
        }

        /* ─── 9d. Phase 8.3.1 — Wöchentliche Kreditzinsen ─── */
        let loansAfter = state.loans;
        let interestAccrued = 0;
        let loansMutated = false;
        if (state.mode !== 'sandbox' && state.loans.length > 0) {
          loansAfter = state.loans.map((loan) => {
            let l = loan;
            while (nextGameTime >= l.lastInterestAt + GAME_WEEK_MS) {
              const r = applyWeeklyInterest(l);
              l = r.loan;
              interestAccrued += r.interestAdded;
              loansMutated = true;
            }
            return l;
          });
          if (loansMutated && interestAccrued > 0) {
            toastsToPush.push({
              kind: 'warning',
              text: `Bankzins fällig: +${interestAccrued.toLocaleString('de-DE')} € auf offene Kredite`,
            });
            statsDelta = statsDelta ?? {};
            statsDelta.spentOnLoanInterest =
              state.stats.spentOnLoanInterest + interestAccrued;
          }
        }

        /* ─── 10. Commit state ─── */
        const updates: Partial<GameState> = { gameTime: nextGameTime };
        if (anyStationMutated) updates.stations = stations;
        if (anyVehicleMutated) updates.vehicles = vehicles;
        if (anyMissionMutated) updates.missions = missions;
        if (creditsDelta !== 0) updates.credits = state.credits + creditsDelta;
        if (pointsDelta !== 0) updates.points = state.points + pointsDelta;
        if (nextMissionAt !== state.nextMissionAt) updates.nextMissionAt = nextMissionAt;
        if (statsDelta) {
          updates.stats = { ...state.stats, ...statsDelta };
        }
        if (nextUpkeepTickAt !== state.lastUpkeepTickAt) {
          updates.lastUpkeepTickAt = nextUpkeepTickAt;
        }
        if (loansMutated) updates.loans = loansAfter;
        // Phase 9.4 — append new radio requests + GC accepted/old (>10 min game-time).
        if (newRadioRequests.length > 0 || (state.radioRequests?.length ?? 0) > 0) {
          const RADIO_GC_AGE_MS = 10 * 60_000;
          const kept = (state.radioRequests ?? []).filter((r) => {
            // Drop accepted requests immediately, drop pending after 10 min.
            if (r.acceptedAt !== undefined) return false;
            return nextGameTime - r.createdAt < RADIO_GC_AGE_MS;
          });
          updates.radioRequests = [...kept, ...newRadioRequests];
        }

        set(updates);

        // Side effects after commit
        for (const t of toastsToPush) get().pushToast(t);
        const audioOpts = {
          muted: state.uiPrefs.muted,
          masterVolume: state.uiPrefs.audioVolume,
        };
        // Phase 12.2.5 — File-backed sounds where available, synthetic fallback otherwise.
        if (beepNeeded) playSoundEvent('mission-spawn', audioOpts);
        if (escalationToasts.length > 0) playSoundEvent('escalation', audioOpts);
        if (closedSuccessIds.size > 0) playSoundEvent('success', audioOpts);
        if (expiredOpenTitles.length + expiredWorkedTitles.length > 0)
          playSoundEvent('failure', audioOpts);
        if (radioMessages.some((r) => r.sound)) playSoundEvent('radio-bleep', audioOpts);
        if (anyDeparted) playSoundEvent('siren-burst', audioOpts);
      },

      resetGame: (mode) => {
        const current = get();
        const target: GameMode = mode ?? current.mode;
        const prefs = current.uiPrefs;
        const sandbox = current.sandboxConfig;
        set({ ...makeInitialState(target), uiPrefs: prefs, sandboxConfig: sandbox });
      },
    }),
    {
      // Phase 7.1.1 — slot-aware persistence. The active slot id is read from
      // localStorage at module load and used as the persist `name`. SwitchSlot
      // writes the pointer and reloads (handled by the SaveBrowser).
      name: slotKey(readActiveSlotId()),
      version: 16,
      partialize: (state) => ({
        credits: state.credits,
        dailyUpkeep: state.dailyUpkeep,
        points: state.points,
        gameTime: state.gameTime,
        speedMultiplier: state.speedMultiplier,
        mode: state.mode,
        paused: state.paused,
        level: state.level,
        homeCity: state.homeCity,
        stations: state.stations,
        vehicles: state.vehicles,
        missions: state.missions,
        mapCenter: state.mapCenter,
        mapZoom: state.mapZoom,
        uiPrefs: state.uiPrefs,
        sandboxConfig: state.sandboxConfig,
        stats: state.stats,
        activeSlotId: state.activeSlotId,
        // Phase 8.3
        loans: state.loans,
        ...(state.lastUpkeepTickAt !== undefined
          ? { lastUpkeepTickAt: state.lastUpkeepTickAt }
          : {}),
        // Phase 9
        ...(state.pauseUsage ? { pauseUsage: state.pauseUsage } : {}),
        ...(state.radioRequests ? { radioRequests: state.radioRequests } : {}),
        ...(state.usedVehicleListings
          ? { usedVehicleListings: state.usedVehicleListings }
          : {}),
        ...(state.usedListingsRefreshedAt !== undefined
          ? { usedListingsRefreshedAt: state.usedListingsRefreshedAt }
          : {}),
        // Phase 10
        ...(state.courthousePosition
          ? { courthousePosition: state.courthousePosition }
          : {}),
      }),
      onRehydrateStorage: () => (state) => {
        // After rehydration, drop ephemeral animation fields. Vehicles in
        // transit status survive, but useVehicleRouting will refetch their
        // routes and reset legStartedAt.
        if (!state) return;
        state.vehicles = state.vehicles.map((v) =>
          v.routeCoords || v.legStartedAt
            ? {
                ...v,
                routeCoords: undefined,
                routeProgress: undefined,
                legStartedAt: undefined,
                legDurationMs: undefined,
              }
            : v,
        );
      },
      migrate: (persisted, version) => {
        const s = (persisted ?? {}) as Partial<GameState> & Record<string, unknown>;

        // v2 → v3
        if (version < 3) {
          s.dailyUpkeep = (s.dailyUpkeep as number | undefined) ?? 0;
          s.points = (s.points as number | undefined) ?? 0;
          s.uiPrefs = (s.uiPrefs as UiPrefs | undefined) ?? defaultUiPrefs();
        }

        // v3 → v4 — see Block B
        if (version < 4) {
          const stationKindMap: Record<string, StationKind> = {
            'fire-large': 'bf-station',
            'fire-small': 'ff-station',
            ems: 'ems-station',
            hospital: 'hospital',
            'bf-station': 'bf-station',
            'ff-station': 'ff-station',
            'ems-station': 'ems-station',
          };
          const oldStations = (s.stations as Array<Record<string, unknown>> | undefined) ?? [];
          s.stations = oldStations.map((st) => {
            const oldKind = String(st.kind ?? 'ff-station');
            const newKind: StationKind = stationKindMap[oldKind] ?? 'ff-station';
            return {
              ...st,
              kind: newKind,
              level: typeof st.level === 'number' ? st.level : 1,
              buildStartedAt: undefined,
              buildCompleteAt: undefined,
            };
          }) as Station[];

          const vehicleTypeMap: Record<string, VehicleType> = {
            LF10: 'LF',
            HLF20: 'HLF',
            ELW1: 'ELW',
            DLK: 'DLK',
            RW: 'RW',
            TSF: 'TSF',
            KTW: 'KTW',
            RTW: 'RTW',
            NEF: 'NEF',
            ELW: 'ELW',
            MTW: 'MTW',
            PKW: 'PKW',
            TLF: 'TLF',
            HLF: 'HLF',
            LF: 'LF',
            GW: 'GW',
            SW: 'SW',
          };
          const oldVehicles = (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          s.vehicles = oldVehicles.map((v) => {
            const oldType = String(v.type ?? 'LF');
            const newType: VehicleType = vehicleTypeMap[oldType] ?? 'LF';
            const spec = VEHICLE_SPECS[newType];
            return {
              ...v,
              type: newType,
              waterTankCurrent:
                typeof v.waterTankCurrent === 'number'
                  ? (v.waterTankCurrent as number)
                  : spec.waterTankMax,
              waterTankMax: spec.waterTankMax,
              breathingAirCurrent:
                typeof v.breathingAirCurrent === 'number'
                  ? (v.breathingAirCurrent as number)
                  : spec.breathingAirMax,
              breathingAirMax: spec.breathingAirMax,
              condition: typeof v.condition === 'number' ? (v.condition as number) : 1,
            };
          }) as Vehicle[];

          const mode: GameMode = (s.mode as GameMode | undefined) ?? 'normal';
          const startingFloor = STARTING_CREDITS[mode];
          const currentCredits = (s.credits as number | undefined) ?? 0;
          if (mode !== 'sandbox' && currentCredits < startingFloor) {
            s.credits = startingFloor;
            s.pendingMigrationToast =
              'Spielstand auf Version 4 aktualisiert. Startkapital wurde auf das neue Niveau angehoben.';
          } else {
            s.pendingMigrationToast = 'Spielstand auf Version 4 aktualisiert.';
          }

          s.dailyUpkeep = computeDailyUpkeep(
            (s.stations as Station[]) ?? [],
            (s.vehicles as Vehicle[]) ?? [],
          );
        }

        // v4 → v5 — Phase 5.1/5.2
        if (version < 5) {
          const oldMissions = (s.missions as Array<Record<string, unknown>> | undefined) ?? [];
          s.missions = oldMissions.map((m) => {
            const oldStatus = m.status as string;
            const status =
              oldStatus === 'missed' ? 'failed' : (oldStatus as Mission['status']);
            const spawnedAt =
              (m.spawnedAt as number | undefined) ??
              (m.createdAt as number | undefined) ??
              Date.now();
            const arrivedAt = (m.arrivedAt as number | undefined) ??
              (m.onSceneSince as number | undefined);
            const durationMs =
              (m.durationMs as number | undefined) ??
              (m.workDurationMs as number | undefined) ??
              600_000;
            return {
              ...m,
              status,
              spawnedAt,
              arrivedAt,
              durationMs,
              progress: (m.progress as number | undefined) ?? 0,
              catalogId: (m.catalogId as string | undefined) ?? 'legacy',
              category: (m.category as Mission['category'] | undefined) ?? 'fire',
              difficulty:
                (m.difficulty as Mission['difficulty'] | undefined) ?? 'easy',
              requiredVehicles: (m.requiredVehicles as MissionRequirement[] | undefined) ?? [],
              assignedVehicleIds:
                (m.assignedVehicleIds as string[] | undefined) ?? [],
            };
          }) as Mission[];

          s.sandboxConfig = (s.sandboxConfig as SandboxConfig | undefined) ?? defaultSandboxConfig();

          const oldPrefs = (s.uiPrefs as Partial<UiPrefs> | undefined) ?? {};
          s.uiPrefs = {
            ...defaultUiPrefs(),
            ...oldPrefs,
            layerToggles: {
              ...defaultUiPrefs().layerToggles,
              ...(oldPrefs.layerToggles ?? {}),
            },
            muted: oldPrefs.muted ?? false,
          } as UiPrefs;

          const oldStations5 = (s.stations as Station[] | undefined) ?? [];
          s.stations = oldStations5.map((st) =>
            st.kind === 'hospital'
              ? { ...st, currentPatients: st.currentPatients ?? [] }
              : st,
          );
        }

        // v5 → v6 — Phase 5.3/5.4
        if (version < 6) {
          const oldMissions6 = (s.missions as Array<Record<string, unknown>> | undefined) ?? [];
          s.missions = oldMissions6.map((m) => ({
            ...m,
            patientsDelivered: (m.patientsDelivered as number | undefined) ?? 0,
          })) as Mission[];

          const oldVehicles6 = (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          s.vehicles = oldVehicles6.map((v) => ({
            ...v,
            routeCoords: undefined,
            routeProgress: undefined,
            legStartedAt: undefined,
            legDurationMs: undefined,
            destinationHospitalId: undefined,
          })) as Vehicle[];
        }

        // v6 → v7 — Phase 6 polish
        if (version < 7) {
          const oldMissions7 = (s.missions as Array<Record<string, unknown>> | undefined) ?? [];
          s.missions = oldMissions7.map((m) => ({
            ...m,
            escalationCount: (m.escalationCount as number | undefined) ?? 0,
          })) as Mission[];

          const oldVehicles7 = (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          s.vehicles = oldVehicles7.map((v) => ({
            ...v,
            repairWarningShown: (v.repairWarningShown as boolean | undefined) ?? false,
          })) as Vehicle[];

          const oldPrefs7 = (s.uiPrefs as Partial<UiPrefs> | undefined) ?? {};
          const layers = (oldPrefs7.layerToggles ?? defaultUiPrefs().layerToggles) as LayerToggles;
          s.uiPrefs = {
            ...defaultUiPrefs(),
            ...oldPrefs7,
            layerToggles: { ...defaultUiPrefs().layerToggles, ...layers },
            audioVolume: oldPrefs7.audioVolume ?? 0.6,
            timeFormat: oldPrefs7.timeFormat ?? '24h',
            reduceMotion: oldPrefs7.reduceMotion ?? false,
          } as UiPrefs;
        }

        // v7 → v8 — Phase 7
        if (version < 8) {
          const oldMissions8 = (s.missions as Array<Record<string, unknown>> | undefined) ?? [];
          s.missions = oldMissions8.map((m) => ({
            ...m,
            radioMessages: (m.radioMessages as RadioMessage[] | undefined) ?? [],
            escalationHistory: (m.escalationHistory as EscalationEntry[] | undefined) ?? [],
            radioRequestFlags: (m.radioRequestFlags as Mission['radioRequestFlags']) ?? {},
          })) as Mission[];

          const oldVehicles8 = (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          s.vehicles = oldVehicles8.map((v) => ({
            ...v,
            installedModules: (v.installedModules as Vehicle['installedModules']) ?? [],
          })) as Vehicle[];

          const oldPrefs8 = (s.uiPrefs as Partial<UiPrefs> | undefined) ?? {};
          // Pre-existing players have already navigated the game once, so we
          // mark onboarding as completed (per user spec).
          s.uiPrefs = {
            ...defaultUiPrefs(),
            ...oldPrefs8,
            onboardingCompleted: oldPrefs8.onboardingCompleted ?? true,
            layerToggles: {
              ...defaultUiPrefs().layerToggles,
              ...(oldPrefs8.layerToggles ?? {}),
            },
          } as UiPrefs;

          // New top-level fields
          s.npcStations = [];
          s.stats = (s.stats as GameStats | undefined) ?? defaultStats();
          s.activeSlotId = (s.activeSlotId as number | undefined) ?? readActiveSlotId();

          // Slot-meta seed for the migrated state.
          if (typeof window !== 'undefined' && window.localStorage) {
            const homeCity = s.homeCity as { displayName?: string } | undefined;
            writeSlotMeta({
              id: s.activeSlotId as number,
              cityName: homeCity?.displayName ?? null,
              mode: (s.mode as GameMode | undefined) ?? 'normal',
              level: (s.level as number | undefined) ?? 1,
              credits: (s.credits as number | undefined) ?? 0,
              lastPlayed: Date.now(),
            });
          }
        }

        // v8 → v9 — Phase 8 (loans, daily upkeep, station extensions, mission phases)
        if (version < 9) {
          // Loans default to none.
          s.loans = (s.loans as Loan[] | undefined) ?? [];
          // Tagesunterhalt boundary anchored at gameTime so day-1 fires after a full day.
          s.lastUpkeepTickAt =
            (s.lastUpkeepTickAt as number | undefined) ??
            (s.gameTime as number | undefined) ??
            Date.now();
          // Stations: ensure extensions[] exists.
          const oldStations9 = (s.stations as Station[] | undefined) ?? [];
          s.stations = oldStations9.map((st) => ({
            ...st,
            extensions: st.extensions ?? [],
          })) as Station[];
          // Stats: add loan-interest field.
          const oldStats = (s.stats as Partial<GameStats> | undefined) ?? defaultStats();
          s.stats = {
            ...defaultStats(),
            ...oldStats,
            spentOnLoanInterest:
              (oldStats.spentOnLoanInterest as number | undefined) ?? 0,
          } as GameStats;
          // Missions: ensure phase fields exist (phase, fireIntensity, callerStatement).
          const oldMissions9 =
            (s.missions as Array<Record<string, unknown>> | undefined) ?? [];
          s.missions = oldMissions9.map((m) => ({
            ...m,
            // Phase 8.4 — narrative additions, defaults are deliberately conservative
            // so legacy missions just look like already-engaged callouts.
            phase: (m.phase as string | undefined) ?? 'engagement',
            callerStatement: (m.callerStatement as string | undefined) ?? '',
            fireIntensity: (m.fireIntensity as number | undefined) ?? undefined,
          })) as unknown as Mission[];

          s.pendingMigrationToast =
            'Spielstand auf Version 9 aktualisiert (Tagesunterhalt, Kredite, Erweiterungen).';
        }

        // v9 → v10 — Phase 9 (measures, module families, addresses, FWK, mileage, pause budget)
        if (version < 10) {
          // Vehicles: migrate legacy module IDs to new family/variant composite IDs.
          // Modules with no clean mapping are dropped + 100 % refunded.
          const oldVehicles10 =
            (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          let totalRefund = 0;
          s.vehicles = oldVehicles10.map((v) => {
            const installed =
              (v.installedModules as
                | Array<{ id?: string; moduleId?: string; installedAt?: number }>
                | undefined) ?? [];
            const remapped: Array<Record<string, unknown>> = [];
            for (const inst of installed) {
              const oldId = String(inst.moduleId ?? '');
              // Already in new format? Keep as-is.
              if (!oldId.startsWith('mod-')) {
                remapped.push(inst);
                continue;
              }
              const m = migrateLegacyModuleId(oldId);
              if (m === null) {
                totalRefund += 0; // unknown — skip
                continue;
              }
              totalRefund += 0; // mapped cleanly, no refund
              remapped.push({ ...inst, moduleId: m.newId });
            }
            return {
              ...v,
              installedModules: remapped,
              totalKilometers:
                typeof v.totalKilometers === 'number'
                  ? (v.totalKilometers as number)
                  : 0,
            };
          }) as unknown as Vehicle[];
          if (totalRefund > 0 && s.mode !== 'sandbox') {
            s.credits = (s.credits as number | undefined ?? 0) + totalRefund;
          }

          // Missions: ensure measures field exists. Legacy missions stay
          // with no measures → tick falls back to old timer-based progress.
          const oldMissions10 =
            (s.missions as Array<Record<string, unknown>> | undefined) ?? [];
          s.missions = oldMissions10.map((m) => ({
            ...m,
            measures: (m.measures as Mission['measures']) ?? [],
          })) as unknown as Mission[];

          // Stations: address fields default empty (NPC reload will refill).
          const oldStations10 = (s.stations as Station[] | undefined) ?? [];
          s.stations = oldStations10.map((st) => ({
            ...st,
            // Phase 9.5 — backfill totalSpent so existing stations are sellable
            // (estimate: build cost of current tier; not perfect, but better than 0).
            totalSpent:
              typeof st.totalSpent === 'number' ? st.totalSpent : undefined,
          })) as Station[];

          // Pause budget: initialise empty.
          if (!s.pauseUsage) {
            s.pauseUsage = {
              yearStartedAt: (s.gameTime as number | undefined) ?? Date.now(),
              freePausesUsed: 0,
              paidPausesCount: 0,
            };
          }
          // Used market: empty until ensureUsedListings() runs.
          s.usedVehicleListings = (s.usedVehicleListings as never) ?? [];
          s.radioRequests = (s.radioRequests as never) ?? [];

          s.pendingMigrationToast =
            'Spielstand auf Version 10 aktualisiert (Maßnahmen, Modul-Familien, Adressen, FWK).';
        }

        // v10 → v11 — Phase 10 (Polizei)
        if (version < 11) {
          // Vehicles: ensure arrestedPersons is an array.
          const oldVehicles11 =
            (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          s.vehicles = oldVehicles11.map((v) => ({
            ...v,
            arrestedPersons: (v.arrestedPersons as Vehicle['arrestedPersons']) ?? [],
          })) as unknown as Vehicle[];

          // Stations: heldPersons defaults to empty.
          const oldStations11 = (s.stations as Station[] | undefined) ?? [];
          s.stations = oldStations11.map((st) => ({
            ...st,
            heldPersons: st.heldPersons ?? [],
          })) as Station[];

          // Stats: per-org breakdown defaults.
          const oldStats11 = (s.stats as Partial<GameStats> | undefined) ?? defaultStats();
          s.stats = {
            ...defaultStats(),
            ...oldStats11,
            byOrganization: oldStats11.byOrganization ?? {
              fire: defaultOrgStats(),
              ems: defaultOrgStats(),
              police: defaultOrgStats(),
            },
          } as GameStats;

          // SandboxConfig: police share defaults.
          const oldSandbox = (s.sandboxConfig as SandboxConfig | undefined) ?? defaultSandboxConfig();
          s.sandboxConfig = {
            ...defaultSandboxConfig(),
            ...oldSandbox,
            policeMissionSharePct: oldSandbox.policeMissionSharePct ?? 30,
          } as SandboxConfig;

          // Missions: cross-org fields default empty (existing missions keep
          // running with their old measure shapes).
          const oldMissions11 =
            (s.missions as Array<Record<string, unknown>> | undefined) ?? [];
          s.missions = oldMissions11.map((m) => ({
            ...m,
            measures: (m.measures as Mission['measures']) ?? [],
          })) as unknown as Mission[];

          // Synthetic courthouse near city center if missing.
          if (!s.courthousePosition && s.homeCity) {
            const center = (s.homeCity as { center?: { lat: number; lng: number } })
              .center;
            if (center) {
              s.courthousePosition = {
                lat: center.lat + (Math.random() - 0.5) * 0.01,
                lng: center.lng + (Math.random() - 0.5) * 0.01,
              };
            }
          }

          s.pendingMigrationToast =
            'Polizei-Erweiterung aktiv: Polizeiwachen sind als Marker auf der Karte verfügbar.';
        }

        // v11 → v12 — Phase 11 (Bugfixes)
        if (version < 12) {
          // 11.1.2 (N4) — Pause-Budget defaults if missing.
          if (!s.pauseUsage) {
            s.pauseUsage = {
              yearStartedAt: (s.gameTime as number | undefined) ?? Date.now(),
              freePausesUsed: 0,
              paidPausesCount: 0,
            };
          }
          // 11.6 — Speed-Multiplier in Klassik/Realistic auf 1× zurücksetzen.
          if (s.mode !== 'sandbox' && ((s.speedMultiplier as number | undefined) ?? 1) > 1) {
            s.speedMultiplier = 1 as SpeedMultiplier;
          }
          // 11.3.2 — Vehicles ohne callsign bekommen ein Notlösungs-Rufzeichen.
          const oldVehicles12 =
            (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          s.vehicles = oldVehicles12.map((v) => {
            if (v.callsign) return v;
            const id = String(v.id ?? '');
            return {
              ...v,
              callsign: id ? id.slice(-4).toUpperCase() : '----',
            };
          }) as unknown as Vehicle[];
          // 11.4.1 (Bug 5) — Onboarding pro Slot: wenn keine homeCity da ist
          // (Slot war noch nie gespielt), reset onboardingCompleted.
          if (!s.homeCity) {
            const prefs = (s.uiPrefs as Partial<UiPrefs> | undefined) ?? defaultUiPrefs();
            s.uiPrefs = {
              ...defaultUiPrefs(),
              ...prefs,
              onboardingCompleted: false,
            } as UiPrefs;
          }
          // 11.2.1 (N1) — NPC-Wachen-Cache invalidieren (alte Klassifizierungen
          // wegwerfen, damit die neuen Heuristiken zum Tragen kommen).
          if (typeof window !== 'undefined' && window.localStorage) {
            try {
              const keys: string[] = [];
              for (let i = 0; i < window.localStorage.length; i++) {
                const k = window.localStorage.key(i);
                if (k && k.startsWith('npc_stations_')) keys.push(k);
              }
              for (const k of keys) window.localStorage.removeItem(k);
            } catch {
              // ignore localStorage errors (private mode etc.)
            }
          }

          s.pendingMigrationToast =
            'Spielstand auf Version 12 aktualisiert — Bugfixes aktiv.';
        }

        // v12 → v13 — Phase 12 (Assets + Pre-Kauf-Modul-Konfig + Werkstatt-Tausch)
        if (version < 13) {
          // Vehicles: workshopUntilAt + pendingModuleSwap default undefined.
          const oldVehicles13 =
            (s.vehicles as Array<Record<string, unknown>> | undefined) ?? [];
          s.vehicles = oldVehicles13.map((v) => ({
            ...v,
            // explicitly normalise to undefined so the persist layer doesn't carry stale data
            workshopUntilAt: undefined,
            pendingModuleSwap: undefined,
          })) as unknown as Vehicle[];

          // Stats: ensure totalIncome is at least 0 (older slots may have it as undefined).
          if (typeof (s.stats as GameStats | undefined)?.totalIncome !== 'number') {
            s.stats = {
              ...(s.stats as GameStats | undefined ?? defaultStats()),
              totalIncome: 0,
            } as GameStats;
          }

          // Realistic-Bestandsspielstände behalten ihre Credits (per Spec).

          s.pendingMigrationToast =
            'Spielstand auf Version 13 aktualisiert: Asset-Update + Modul-Tausch-Mechanik.';
        }

        // v13 → v14 — Phase 13 (Bugfix-Sammlung + Feature-Wünsche)
        if (version < 14) {
          // 13.10 — uiPrefs.showAcquirableStations default off for migrated slots.
          // 13.7  — waterFeatures default empty (loader hydrates per slot).
          const prevPrefs14 = (s.uiPrefs as Partial<UiPrefs> | undefined) ?? defaultUiPrefs();
          s.uiPrefs = {
            ...defaultUiPrefs(),
            ...prevPrefs14,
            showAcquirableStations:
              typeof prevPrefs14.showAcquirableStations === 'boolean'
                ? prevPrefs14.showAcquirableStations
                : false,
            // 13.11 — `missions` layer toggle is no longer used by the toolbar,
            // but we keep the field in LayerToggles for compat. Default true so
            // any leftover code paths still render markers.
            layerToggles: {
              ...defaultUiPrefs().layerToggles,
              ...(prevPrefs14.layerToggles ?? {}),
              missions: true,
            },
          } as UiPrefs;

          // 13.7 — initialise waterFeatures (the loader hook will hydrate it on
          // mount; we just need the field to exist).
          if (!Array.isArray((s as Record<string, unknown>).waterFeatures)) {
            (s as Record<string, unknown>).waterFeatures = [];
          }

          // 13.1 — NPC-Cache invalidieren (Dedupe-Heuristik wirkt erst nach
          // Reload aus OSM oder Cache-Reuse mit dedupeNpcStations()).
          if (typeof window !== 'undefined' && window.localStorage) {
            try {
              const keys: string[] = [];
              for (let i = 0; i < window.localStorage.length; i++) {
                const k = window.localStorage.key(i);
                if (k && k.startsWith('npc_stations_')) keys.push(k);
              }
              for (const k of keys) window.localStorage.removeItem(k);
            } catch {
              // ignore localStorage errors (private mode etc.)
            }
          }

          s.pendingMigrationToast = 'Spielstand auf Version 14 aktualisiert';
        }

        // v14 → v15 — Phase 15 (Closed-Beta-Setup)
        if (version < 15) {
          // 15.9 — `beta`-Slice initialisieren falls noch nicht vorhanden.
          const existingBeta = (s as Record<string, unknown>).beta as
            | { onboardingShown?: unknown; testerPseudonym?: unknown }
            | undefined;
          const onboardingShown =
            typeof existingBeta?.onboardingShown === 'boolean' ? existingBeta.onboardingShown : false;
          const existingPseudonym =
            typeof existingBeta?.testerPseudonym === 'string' && existingBeta.testerPseudonym
              ? existingBeta.testerPseudonym
              : undefined;
          // Inline-Pseudonym-Generator (vermeidet Cycle mit `lib/errorLogger`).
          const generated = `Tester-${Math.floor(Math.random() * 0xffff)
            .toString(16)
            .toUpperCase()
            .padStart(4, '0')}`;
          (s as Record<string, unknown>).beta = {
            onboardingShown,
            testerPseudonym: existingPseudonym ?? generated,
          };
          s.pendingMigrationToast =
            'Spielstand auf Version 15 aktualisiert: Closed-Beta-Modus.';
        }

        // v15 → v16 — Phase 16 (Beta-Login + Cloud-Sync)
        if (version < 16) {
          // 16.7 — `lastCloudSyncAt` Default undefined; wird beim ersten Sync
          // gesetzt. authStore/cloudSyncStore leben separat und brauchen keine
          // gameStore-Migration.
          if ((s as Record<string, unknown>).lastCloudSyncAt === undefined) {
            (s as Record<string, unknown>).lastCloudSyncAt = undefined;
          }
          s.pendingMigrationToast =
            'Closed Beta v16: Beta-Login + Cloud-Sync aktiv.';
        }

        return s as unknown as GameState;
      },
    },
  ),
);

export { getMaxTierLevel };
